import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment';
import { brand } from '../../config/brand';
import { useHistory, useLocation } from 'react-router-dom';
import SimulatedTweet from '../SimulatedTweet';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "../ui/hover-card";
import {
  X,
  Sparkles,
  Clock,
  Send,
  Loader2,
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
  Tags,
  MessageSquare,
  MessageSquareReply,
  Gamepad2,
  Heart,
  MessageCircle,
  Repeat2,
  Eye,
  Activity,
  TrendingUp,
  RefreshCw,
  ExternalLink,
  Trash2,
  Youtube,
  Instagram,
  Facebook,
  Music2,
  Cloud,
  Share2,
  ChevronRight,
  ImageIcon,
  Paperclip,
  Users,
  Flame,
  Settings,
  Info,
  Globe,
  Plus
} from 'lucide-react';
import { usePostModal } from '../../contexts/PostModalContext';
import { useProjects } from '../../contexts/ProjectContext';
import { useGlobalState } from '../../contexts/GlobalStateContext';
import UniversalEmbedPlayer from '../UniversalEmbedPlayer';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from "../../lib/utils";
import MultiMediaPreview from '../MultiMediaPreview';
import VideoCropper from '../VideoCropper';
import AccountTextInput from '../AccountTextInput';
import UploadButton from '../UploadButton';
import SocialMediaSelector from '../SocialMediaSelector';
import PlatformValidations from '../PlatformValidations';
import UploadProgressDialog from '../UploadProgressDialog';
import SocialAvatar from '../SocialAvatar';
import { getPlatformRequirement, hasBlockingMediaIssues } from '../../utils/platformRequirements';
import { getPlatformIcon as getPlatformIconUtil, getPlatformColor as getPlatformColorUtil } from '../../utils/platformIcons';
import { MediaItem, createMediaItem, generateVideoThumbnail } from '../../types/media';

// shadcn UI
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from "../ui/card";
import {
  Avatar,
  AvatarImage,
  AvatarFallback
} from "../ui/avatar";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Badge } from "../ui/badge";
import { Textarea } from "../ui/textarea";
import { Separator } from "../ui/separator";
import { Checkbox } from "../ui/checkbox";
import { Switch } from "../ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { ScrollArea } from "../ui/scroll-area";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { Progress } from "../ui/progress";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { toast } from "sonner";
import { franc } from 'franc-min';
import { API_BASE_URL } from '../../config';

interface UploadProgress {
  progress: number;
  status: string;
  message: string;
  platformStatus?: Record<string, { status: 'pending' | 'uploading' | 'completed' | 'error'; errorMessage?: string }>;
}

const EngagementGraph = ({ schedules }: { schedules: any[] }) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  // Group and sum snapshots by timestamp to handle multiple platforms
  const rawSnapshots = (schedules || [])
    .flatMap(s => (s.snapshots || []).map((sn: any) => ({ ...sn })));

  const groupedSnapshots = rawSnapshots.reduce((acc: any[], s) => {
    const time = new Date(s.timestamp).getTime();
    const existing = acc.find(item => new Date(item.timestamp).getTime() === time);
    if (existing) {
      existing.views += (s.views || 0);
      existing.likes += (s.likes || 0);
      existing.bookmarks += (s.bookmarks || 0);
    } else {
      acc.push({
        timestamp: s.timestamp,
        views: s.views || 0,
        likes: s.likes || 0,
        bookmarks: s.bookmarks || 0
      });
    }
    return acc;
  }, []).sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

  if (groupedSnapshots.length < 2) {
    return (
      <div className="h-32 flex flex-col items-center justify-center border border-dashed border-border rounded-2xl bg-muted/20 text-muted-foreground space-y-2">
        <Activity className="h-5 w-5 opacity-20" />
        <span className="text-[10px] uppercase font-black tracking-widest">Awaiting more data...</span>
      </div>
    );
  }

  // Calculate "Likes per Hour" deltas
  const allSnapshots = groupedSnapshots.map((s, i) => {
    if (i === 0) return { ...s, likesPerHour: 0 };
    const prev = groupedSnapshots[i - 1];
    const diffMs = new Date(s.timestamp).getTime() - new Date(prev.timestamp).getTime();
    const diffHours = diffMs / (1000 * 60 * 60);
    const likesDiff = Math.max(0, s.likes - prev.likes);
    const likesPerHour = diffHours > 0.01 ? likesDiff / diffHours : 0;
    return { ...s, likesPerHour };
  });

  // Get min/max for scaling
  const maxViews = Math.max(...allSnapshots.map(s => s.views || 0), 10);
  const maxLikes = Math.max(...allSnapshots.map(s => s.likes || 0), 5);
  const maxLikesPerHour = Math.max(...allSnapshots.map(s => s.likesPerHour || 0), 1);

  const startTime = new Date(allSnapshots[0].timestamp).getTime();
  const endTime = new Date(allSnapshots[allSnapshots.length - 1].timestamp).getTime();
  const duration = endTime - startTime || 1;

  // SVG Config
  const width = 400;
  const height = 100;
  const padding = 10;

  const getPoints = (data: any[], key: string, max: number) => {
    return data.map((s, i) => {
      const x = ((new Date(s.timestamp).getTime() - startTime) / duration) * (width - 2 * padding) + padding;
      const val = s[key] || 0;
      const y = height - ((val / max) * (height - 2 * padding) + padding);
      return `${x},${y}`;
    }).join(' ');
  };

  const viewPoints = getPoints(allSnapshots, 'views', maxViews);
  const likePoints = getPoints(allSnapshots, 'likes', maxLikes);
  const ratePoints = getPoints(allSnapshots, 'likesPerHour', maxLikesPerHour);

  return (
    <div className="space-y-3 relative">
      <div className="flex items-center justify-between px-1">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 text-[10px] font-black text-muted-foreground uppercase tracking-widest">
            <div className="h-1.5 w-1.5 rounded-full bg-primary" />
            Views
          </div>
          <div className="flex items-center gap-1.5 text-[10px] font-black text-muted-foreground uppercase tracking-widest">
            <div className="h-1.5 w-1.5 rounded-full bg-rose-500" />
            Likes
          </div>
          <div className="flex items-center gap-1.5 text-[10px] font-black text-muted-foreground uppercase tracking-widest">
            <div className="h-1.5 w-1.5 rounded-full bg-amber-500" />
            Likes/Hr
          </div>
        </div>
        <div className="text-[9px] text-muted-foreground font-bold uppercase tracking-tighter">
          {moment(allSnapshots[0].timestamp).format('MMM D, HH:mm')} → {moment(allSnapshots[allSnapshots.length - 1].timestamp).format('HH:mm')}
        </div>
      </div>

      <div className="relative h-28 w-full bg-background/30 rounded-xl border border-border/50 overflow-visible group">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="w-full h-full preserve-3d overflow-visible"
          preserveAspectRatio="none"
          onMouseLeave={() => setHoveredIndex(null)}
        >
          <defs>
            <linearGradient id="viewGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgb(59, 130, 246)" stopOpacity="0.2" />
              <stop offset="100%" stopColor="rgb(59, 130, 246)" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="likeGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgb(244, 63, 94)" stopOpacity="0.2" />
              <stop offset="100%" stopColor="rgb(244, 63, 94)" stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Growth Rate (Likes/Hr) - Area beneath is more visual for spikes */}
          <path
            d={`M ${ratePoints.split(' ')[0].split(',')[0]},${height} L ${ratePoints} L ${ratePoints.split(' ').pop()?.split(',')[0]},${height} Z`}
            fill="rgb(245, 158, 11, 0.1)"
          />
          <polyline
            fill="none"
            stroke="#f59e0b"
            strokeWidth="1.5"
            strokeDasharray="4 2"
            strokeLinecap="round"
            strokeLinejoin="round"
            points={ratePoints}
            className="opacity-50"
          />

          {/* Views Path */}
          <path
            d={`M ${viewPoints.split(' ')[0].split(',')[0]},${height} L ${viewPoints} L ${viewPoints.split(' ').pop()?.split(',')[0]},${height} Z`}
            fill="url(#viewGradient)"
          />
          <polyline
            fill="none"
            stroke="hsl(var(--primary))"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            points={viewPoints}
            className="drop-shadow-[0_0_8px_hsl(var(--primary)/0.3)]"
          />

          {/* Likes Path */}
          <path
            d={`M ${likePoints.split(' ')[0].split(',')[0]},${height} L ${likePoints} L ${likePoints.split(' ').pop()?.split(',')[0]},${height} Z`}
            fill="url(#likeGradient)"
          />
          <polyline
            fill="none"
            stroke="#f43f5e"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            points={likePoints}
            className="drop-shadow-[0_0_8px_rgba(244,63,94,0.3)]"
          />

          {/* Interaction Rects */}
          {allSnapshots.map((s, i) => {
            const x = ((new Date(s.timestamp).getTime() - startTime) / duration) * (width - 2 * padding) + padding;
            return (
              <rect
                key={`rect-${i}`}
                x={x - (width / allSnapshots.length) / 2}
                y={0}
                width={width / allSnapshots.length}
                height={height}
                fill="transparent"
                onMouseEnter={() => setHoveredIndex(i)}
                className="cursor-pointer"
              />
            );
          })}

          {/* Points */}
          {allSnapshots.map((s, i) => {
            const x = ((new Date(s.timestamp).getTime() - startTime) / duration) * (width - 2 * padding) + padding;
            const yView = height - ((s.views / maxViews) * (height - 2 * padding) + padding);
            const yLike = height - ((s.likes / maxLikes) * (height - 2 * padding) + padding);
            const yRate = height - ((s.likesPerHour / maxLikesPerHour) * (height - 2 * padding) + padding);
            const isHovered = hoveredIndex === i;

            return (
              <React.Fragment key={i}>
                <circle
                  cx={x}
                  cy={yView}
                  r={isHovered ? 4 : 2.5}
                  className={cn(
                    "fill-primary transition-all duration-200",
                    isHovered ? "opacity-100 ring-4 ring-primary/20" : "opacity-0 group-hover:opacity-100"
                  )}
                />
                <circle
                  cx={x}
                  cy={yLike}
                  r={isHovered ? 4 : 2.5}
                  className={cn(
                    "fill-rose-500 transition-all duration-200",
                    isHovered ? "opacity-100 ring-4 ring-rose-500/20" : "opacity-0 group-hover:opacity-100"
                  )}
                />
                {isHovered && (
                  <circle
                    cx={x}
                    cy={yRate}
                    r={3}
                    className="fill-amber-500 ring-4 ring-amber-500/20"
                  />
                )}
              </React.Fragment>
            );
          })}
        </svg>

        {/* Tooltip */}
        {hoveredIndex !== null && (
          <div
            className="absolute z-50 pointer-events-none bg-muted border border-border p-2.5 rounded-lg shadow-2xl text-[10px] space-y-1.5 animate-in fade-in zoom-in-95 duration-100"
            style={{
              left: `${((new Date(allSnapshots[hoveredIndex].timestamp).getTime() - startTime) / duration) * 100}%`,
              top: '50%',
              transform: 'translate(-50%, -120%)'
            }}
          >
            <div className="font-black text-muted-foreground uppercase tracking-widest border-b border-border pb-1 mb-1">
              {moment(allSnapshots[hoveredIndex].timestamp).format('MMM D, HH:mm')}
            </div>
            <div className="flex items-center justify-between gap-8">
              <span className="text-muted-foreground font-bold uppercase tracking-widest">Views</span>
              <span className="text-primary font-black">{allSnapshots[hoveredIndex].views.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between gap-8">
              <span className="text-muted-foreground font-bold uppercase tracking-widest">Likes</span>
              <span className="text-rose-400 font-black">{allSnapshots[hoveredIndex].likes.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between gap-8 pt-1 border-t border-border/50">
              <span className="text-amber-500/70 font-bold uppercase tracking-widest">Avg. Likes/Hr</span>
              <span className="text-amber-400 font-black">{allSnapshots[hoveredIndex].likesPerHour.toFixed(1)}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const getMetricLabel = (platform: string, metric: string) => {
  const p = platform.toLowerCase();
  if (p.includes('telegram') && metric === 'likes') return 'Reactions';
  return metric.charAt(0).toUpperCase() + metric.slice(1);
};

const getMetricUnsupportedReason = (platform: string, metric: string, schedule?: any) => {
  const p = platform.toLowerCase();
  if (p.includes('telegram')) {
    const chatId = schedule?.socialAccount?.externalId || '';
    const isBasicGroup = chatId && !chatId.startsWith('-100');
    if (isBasicGroup) {
      return "Basic groups don't support public metrics. Convert to a Supergroup to enable them.";
    }
    if (metric === 'shares') return "Telegram doesn't provide forward counts via Bot API";
  }
  if (p.includes('discord')) return "Discord doesn't provide public metrics for individual messages";
  if (p.includes('instagram') && metric === 'shares') return "Instagram doesn't provide share counts via API";
  if (p.includes('bluesky') && metric === 'views') return "Bluesky doesn't track view counts";
  if (p.includes('tumblr') && (metric === 'comments' || metric === 'views')) return "Metric not available for Tumblr";
  if (p.includes('youtube') && metric === 'shares') return "YouTube doesn't provide share counts for individual videos";

  return "Not supported by this platform";
};

const isMetricSupported = (platform: string, metric: 'likes' | 'comments' | 'shares' | 'views', schedule?: any) => {
  const p = platform.toLowerCase();

  // Telegram specific logic for basic groups
  if (p.includes('telegram')) {
    const chatId = schedule?.socialAccount?.externalId || '';
    const isBasicGroup = chatId && !chatId.startsWith('-100');
    if (isBasicGroup) return false;
    // Comments are now supported via bot webhook for supergroups
    return metric === 'likes' || metric === 'views' || metric === 'comments';
  }

  if (p.includes('twitter') || p.includes('x')) return true;
  if (p.includes('tiktok')) return true;
  if (p.includes('threads')) return true;

  if (p.includes('instagram')) {
    return metric !== 'shares';
  }
  if (p.includes('bluesky')) {
    return metric !== 'views';
  }
  if (p.includes('tumblr')) {
    return metric === 'likes' || metric === 'shares';
  }
  if (p.includes('youtube')) {
    return metric !== 'shares';
  }
  if (p.includes('discord')) {
    return false;
  }
  return true;
};

export default function PostModal(): JSX.Element {
  const { isOpen, isReadMode, currentPost, setCurrentPost, openPostById, openMediaById, closePostModal } = usePostModal();
  const { activeProject, refreshProjects } = useProjects();
  const { localStorage: globalState, updateLocalStorage, refreshUser, user, setUser } = useGlobalState();
  const selectedPlatforms = globalState.selectedPlatforms || [];
  const debugMode = globalState.debugMode;
  const queryClient = useQueryClient();
  const history = useHistory();
  const location = useLocation();

  const formatFollowers = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const getPlatformIcon = getPlatformIconUtil;
  const getPlatformColor = getPlatformColorUtil;

  const token = localStorage.getItem('token');
  const [backgroundPath, setBackgroundPath] = useState<string | null>(null);
  const [mediaLoadError, setMediaLoadError] = useState(false);

  // URL Handling: Listen for permalinks
  useEffect(() => {
    const postMatch = location.pathname.match(/\/posts\/([a-zA-Z0-9-]+)/);
    const mediaMatch = location.pathname.match(/\/media\/([a-zA-Z0-9-]+)/);

    if (postMatch && (!currentPost || currentPost.id !== postMatch[1])) {
      openPostById(postMatch[1]);
    } else if (mediaMatch && (!currentPost || currentPost.mediaPath?.includes(mediaMatch[1]))) {
      // If we're on a media URL, open by media ID
      // We don't have currentPost.mediaId, but we can check if it's already open
      openMediaById(mediaMatch[1]);
    } else if (!postMatch && !mediaMatch && isOpen && isReadMode) {
      // If user navigated away but modal is open in read mode, close it
      closePostModal();
    }
  }, [location.pathname]);

  // URL Handling: Update URL when modal opens/closes
  useEffect(() => {
    if (isOpen && currentPost?.id && isReadMode) {
      const currentPath = location.pathname;
      // If it's already a post or media link, don't override it unless it's the wrong ID
      if (!currentPath.includes(`/posts/${currentPost.id}`) && !currentPath.includes('/media/')) {
        // Save the background path if we're not already on a permalink
        if (!currentPath.match(/\/posts\/[a-zA-Z0-9-]+/) && !currentPath.match(/\/media\/[a-zA-Z0-9-]+/)) {
          setBackgroundPath(currentPath);
        }
        history.push(`/posts/${currentPost.id}`);
      }
    } else if (!isOpen && (location.pathname.includes('/posts/') || location.pathname.includes('/media/'))) {
      // When modal is closed, go back to the background path if we have one, otherwise fallback to root
      if (backgroundPath) {
        history.push(backgroundPath);
        setBackgroundPath(null);
      } else {
        // Safe fallback: if we were on /posts/ID, go to /posts. If on /media/ID, go to /my-media
        if (location.pathname.includes('/posts/')) {
          history.push('/posts');
        } else if (location.pathname.includes('/media/')) {
          history.push('/my-media');
        } else {
          history.push('/');
        }
      }
    }
  }, [isOpen, currentPost?.id, isReadMode]);

  // Local state for the form
  // mediaPath, mediaPreview, localFile are now derived from mediaItems (see below)
  const [translations, setTranslations] = useState<Record<string, { text: string, tiktokTitle?: string }>>({});
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [postMode, setPostMode] = useState<'now' | 'schedule'>('now');
  const [scheduledAt, setScheduledAt] = useState<string>(moment().add(1, 'hour').format('YYYY-MM-DDTHH:mm'));
  const [trendId, setTrendId] = useState<string | null>(null);
  const [selectedScheduleId, setSelectedScheduleId] = useState<string | 'all'>('all');

  // Multi-media support - mediaItems is the single source of truth for all media
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);

  // Platforms that can't handle the current multi-media configuration (before/after posts)
  const multiMediaDisabled = useMemo(() => {
    if (mediaItems.length < 2) return { ids: [] as string[], reason: '' };

    const videoCount = mediaItems.filter(m => m.type === 'video').length;
    const imageCount = mediaItems.filter(m => m.type === 'image').length;

    const disabledIds: string[] = [];
    const accounts = activeProject?.socialAccounts || [];
    for (const sa of accounts) {
      const req = getPlatformRequirement(sa.socialAccount.platform);
      if (!req) { disabledIds.push(sa.socialAccount.id); continue; }

      // Check if platform can handle the video count
      if (videoCount > 0 && req.maxVideos < videoCount) {
        disabledIds.push(sa.socialAccount.id); continue;
      }
      // Check if platform can handle the image count
      if (imageCount > 0 && req.maxImages < imageCount) {
        disabledIds.push(sa.socialAccount.id); continue;
      }
      // Check if platform supports mixed media when both types are present
      if (videoCount > 0 && imageCount > 0 && !req.allowMixedMedia) {
        disabledIds.push(sa.socialAccount.id); continue;
      }
    }

    if (disabledIds.length === 0) return { ids: [] as string[], reason: '' };

    const reason = videoCount >= 2
      ? 'This platform does not support multiple videos in one post'
      : imageCount >= 2
        ? 'This platform does not support multiple images in one post'
        : 'This platform does not support this media combination';
    return { ids: disabledIds, reason };
  }, [mediaItems, activeProject]);

  // Auto-deselect platforms that can't handle current multi-media config
  useEffect(() => {
    if (multiMediaDisabled.ids.length > 0) {
      const filtered = selectedPlatforms.filter(id => !multiMediaDisabled.ids.includes(id));
      if (filtered.length !== selectedPlatforms.length) {
        updateLocalStorage({ selectedPlatforms: filtered });
      }
    }
  }, [multiMediaDisabled.ids]);

  const [evergreenTags, setEvergreenTags] = useState<string[]>([]);
  const [inactiveTags, setInactiveTags] = useState<string[]>([]);
  const [isEvergreenSettingsOpen, setIsEvergreenSettingsOpen] = useState(false);
  const [evergreenInput, setEvergreenInput] = useState('');
  const handleTranslate = async (sourceLang: string) => {
    const sourceText = translations[sourceLang]?.text;
    if (!sourceText) return;

    // Get all languages from selected social accounts
    const targetLangs = new Set<string>();
    selectedPlatforms.forEach(id => {
      const acc = activeProject?.socialAccounts?.find(sa => sa.socialAccount.id === id);
      if (acc && acc.socialAccount.language && acc.socialAccount.language !== sourceLang) {
        targetLangs.add(acc.socialAccount.language);
      }
    });

    if (targetLangs.size === 0) {
      toast.info('No other languages needed for selected accounts');
      return;
    }

    setIsTranslating(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/ai/translate`, {
        text: sourceText,
        sourceLang,
        targetLangs: Array.from(targetLangs)
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      const newTranslations = { ...translations };
      const apiTranslations = response.data.translations;

      Object.keys(apiTranslations).forEach(lang => {
        newTranslations[lang] = {
          ...newTranslations[lang],
          text: apiTranslations[lang].text,
          tiktokTitle: apiTranslations[lang].tiktokTitle || newTranslations[lang]?.tiktokTitle
        };
      });

      setTranslations(newTranslations);
      toast.success('Translations completed!');
    } catch (error: any) {
      console.error('Translation failed:', error);
      toast.error(error.response?.data?.error || 'Failed to translate');
    } finally {
      setIsTranslating(false);
    }
  };

  const [isTranslating, setIsTranslating] = useState(false);

  // Helper to map franc ISO 639-3 to ISO 639-1
  const langMap3to2: Record<string, string> = useMemo(() => ({
    'eng': 'en',
    'spa': 'es',
    'fra': 'fr',
    'por': 'pt',
    'deu': 'de',
    'ita': 'it',
    'jpn': 'ja',
    'zho': 'zh',
    'rus': 'ru',
    'kor': 'ko'
  }), []);

  const languageNames: Record<string, string> = useMemo(() => ({
    'en': 'English',
    'es': 'Spanish',
    'fr': 'French',
    'pt': 'Portuguese',
    'de': 'German',
    'it': 'Italian',
    'ja': 'Japanese',
    'zh': 'Chinese',
    'ru': 'Russian',
    'ko': 'Korean',
  }), []);

  // Helper to get languages needed based on selected platforms
  const languagesNeeded = useMemo(() => {
    const langs: string[] = [];
    const langSet = new Set<string>();

    if (selectedPlatforms.length > 0) {
      selectedPlatforms.forEach(id => {
        const acc = activeProject?.socialAccounts?.find(sa => sa.socialAccount.id === id);
        if (acc && acc.socialAccount.language) {
          if (!langSet.has(acc.socialAccount.language)) {
            langSet.add(acc.socialAccount.language);
            langs.push(acc.socialAccount.language);
          }
        }
      });
    }

    // If no languages found from platforms, default to English
    if (langs.length === 0) {
      langs.push('en');
    }

    return langs;
  }, [selectedPlatforms, activeProject]);

  // Sync translations with languagesNeeded when not in read mode
  // We avoid depending on 'translations' to prevent running on every keystroke
  useEffect(() => {
    if (!isReadMode) {
      setTranslations(prev => {
        const newTranslations = { ...prev };
        let changed = false;

        // Add missing languages
        languagesNeeded.forEach(lang => {
          if (!newTranslations[lang]) {
            newTranslations[lang] = { text: '', tiktokTitle: '' };
            changed = true;
          }
        });

        // Remove languages NOT in languagesNeeded if they are empty
        Object.keys(newTranslations).forEach(lang => {
          if (!languagesNeeded.includes(lang) && !newTranslations[lang].text?.trim()) {
            delete newTranslations[lang];
            changed = true;
          }
        });

        return changed ? newTranslations : prev;
      });
    }
  }, [languagesNeeded, isReadMode]);

  const [baseLanguage, setBaseLanguage] = useState<string>('en');
  const userSwappedRef = useRef(false);

  // Follow the first needed language automatically, unless the user manually swapped or we are in read mode
  useEffect(() => {
    if (languagesNeeded.length > 0 && !userSwappedRef.current && !isReadMode) {
      if (baseLanguage !== languagesNeeded[0]) {
        setBaseLanguage(languagesNeeded[0]);
      }
    }
  }, [languagesNeeded, isReadMode, baseLanguage]);

  // Reset swap override when modal closes
  useEffect(() => {
    if (!isOpen) {
      userSwappedRef.current = false;
    }
  }, [isOpen]);

  // When modal opens in create mode, set source language to first in list (only on open, not on every languagesNeeded change)
  const prevIsOpenRef = useRef(false);
  useEffect(() => {
    const justOpened = isOpen && !prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;
    if (justOpened && !isReadMode && languagesNeeded.length > 0) {
      setBaseLanguage(languagesNeeded[0]);
    }
  }, [isOpen, isReadMode, languagesNeeded]);


  // TikTok Specific State
  const [tiktokTitle, setTiktokTitle] = useState<string>('');
  const [tiktokPrivacy, setTiktokPrivacy] = useState<string>(''); // No default
  const [tiktokAllowComments, setTiktokAllowComments] = useState<boolean>(false);
  const [tiktokAllowDuet, setTiktokAllowDuet] = useState<boolean>(false);
  const [tiktokAllowStitch, setTiktokAllowStitch] = useState<boolean>(false);
  const [tiktokContentDisclosure, setTiktokContentDisclosure] = useState<boolean>(false);
  const [tiktokYourBrand, setTiktokYourBrand] = useState<boolean>(false);
  const [tiktokBrandedContent, setTiktokBrandedContent] = useState<boolean>(false);
  const [showTikTokConfirmation, setShowTikTokConfirmation] = useState<boolean>(false);

  // Tumblr Specific State
  const [tumblrContentDisclosure, setTumblrContentDisclosure] = useState<boolean>(false);
  const [tumblrYourBrand, setTumblrYourBrand] = useState<boolean>(false);
  const [tumblrBrandedContent, setTumblrBrandedContent] = useState<boolean>(false);

  // YouTube Specific State
  const [youtubeVideoType, setYoutubeVideoType] = useState<'video' | 'short'>('short');
  const [youtubeTitle, setYoutubeTitle] = useState<string>('');

  const selectedTikTokAccounts = useMemo(() => {
    return activeProject?.socialAccounts?.filter(sa =>
      selectedPlatforms.includes(sa.socialAccount.id) &&
      sa.socialAccount.platform.toLowerCase().includes('tiktok')
    ) || [];
  }, [activeProject, selectedPlatforms]);

  const hasTikTokSelected = selectedTikTokAccounts.length > 0;

  // YouTube Fallback — project-level
  const youtubeAccountsForProject = useMemo(() => {
    return activeProject?.socialAccounts
      ?.filter((sa: any) => sa.socialAccount.platform === 'youtube' && sa.socialAccount.isValid !== false)
      .map((sa: any) => sa.socialAccount) || [];
  }, [activeProject]);

  const [ytFallbackEnabled, setYtFallbackEnabled] = useState(false);
  const [ytFallbackAccountId, setYtFallbackAccountId] = useState<string | null>(null);

  useEffect(() => {
    setYtFallbackEnabled(activeProject?.youtubeFallbackEnabled || false);
    setYtFallbackAccountId(activeProject?.youtubeFallbackAccountId || null);
  }, [activeProject?.youtubeFallbackEnabled, activeProject?.youtubeFallbackAccountId]);

  const handleYtFallbackChange = async (enabled: boolean, accountId?: string) => {
    if (!activeProject) return;
    setYtFallbackEnabled(enabled);
    if (accountId !== undefined) setYtFallbackAccountId(accountId || null);
    try {
      await axios.patch(
        `${API_BASE_URL}/games/${activeProject.id}/youtube-fallback`,
        { enabled, accountId: accountId ?? (enabled ? youtubeAccountsForProject[0]?.id : null) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      refreshProjects();
    } catch {
      toast.error("Failed to update YouTube fallback");
    }
  };

  const [tiktokCreatorInfo, setTiktokCreatorInfo] = useState<Record<string, any>>({});
  const [isFetchingTiktokInfo, setIsFetchingTiktokInfo] = useState<boolean>(false);

  // Merged restrictions for all selected TikTok accounts
  const tiktokRestrictions = useMemo(() => {
    let commentDisabled = false;
    let duetDisabled = false;
    let stitchDisabled = false;
    let maxDuration = 600; // Default 10 min
    let allowedPrivacy: string[] = ['PUBLIC_TO_EVERYONE', 'MUTUAL_FOLLOW_FRIENDS', 'SELF_ONLY'];

    selectedTikTokAccounts.forEach(account => {
      const infoResponse = tiktokCreatorInfo[account.socialAccount.id];
      const info = infoResponse?.data;
      if (info) {
        if (info.comment_disabled) commentDisabled = true;
        if (info.duet_disabled) duetDisabled = true;
        if (info.stitch_disabled) stitchDisabled = true;
        if (info.max_video_post_duration_sec && info.max_video_post_duration_sec < maxDuration) {
          maxDuration = info.max_video_post_duration_sec;
        }
        if (info.privacy_level_options) {
          allowedPrivacy = allowedPrivacy.filter(p => info.privacy_level_options.includes(p));
        }
      }
    });

    return { commentDisabled, duetDisabled, stitchDisabled, maxDuration, allowedPrivacy };
  }, [selectedTikTokAccounts, tiktokCreatorInfo]);

  const globalMaxDuration = useMemo(() => {
    let minLimit = Infinity;

    // Check selected platforms from static requirements
    selectedPlatforms.forEach(id => {
      const acc = activeProject?.socialAccounts?.find(sa => sa.socialAccount.id === id);
      if (acc) {
        const req = getPlatformRequirement(acc.socialAccount.platform);
        if (req && req.maxVideoDuration < minLimit) {
          minLimit = req.maxVideoDuration;
        }
      }
    });

    // If TikTok is selected, its dynamic restriction might be even lower
    if (hasTikTokSelected && tiktokRestrictions.maxDuration < minLimit) {
      minLimit = tiktokRestrictions.maxDuration;
    }

    // Default to 140s (X limit) if no other limit is found or selected
    return minLimit === Infinity ? 140 : minLimit;
  }, [selectedPlatforms, activeProject, hasTikTokSelected, tiktokRestrictions.maxDuration]);

  useEffect(() => {
    if (hasTikTokSelected && isOpen) {
      const fetchCreatorInfo = async () => {
        setIsFetchingTiktokInfo(true);
        const newInfo: Record<string, any> = { ...tiktokCreatorInfo };
        let changed = false;

        for (const account of selectedTikTokAccounts) {
          const accountId = account.socialAccount.id;
          if (!newInfo[accountId]) {
            try {
              const res = await axios.get(`${API_BASE_URL}/social-accounts/${accountId}/tiktok-creator-info`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              if (res.data) {
                newInfo[accountId] = res.data;
                changed = true;
              }
            } catch (err) {
              console.error(`Error fetching TikTok creator info for ${accountId}:`, err);
            }
          }
        }

        if (changed) {
          setTiktokCreatorInfo(newInfo);
        }
        setIsFetchingTiktokInfo(false);
      };

      fetchCreatorInfo();
    }
  }, [hasTikTokSelected, isOpen, selectedTikTokAccounts, token]);

  const selectedYouTubeAccounts = useMemo(() => {
    return activeProject?.socialAccounts?.filter(sa =>
      selectedPlatforms.includes(sa.socialAccount.id) &&
      sa.socialAccount.platform.toLowerCase().includes('youtube')
    ) || [];
  }, [activeProject, selectedPlatforms]);

  const hasYouTubeSelected = selectedYouTubeAccounts.length > 0;

  const selectedTumblrAccounts = useMemo(() => {
    return activeProject?.socialAccounts?.filter(sa =>
      selectedPlatforms.includes(sa.socialAccount.id) &&
      sa.socialAccount.platform.toLowerCase().includes('tumblr')
    ) || [];
  }, [activeProject, selectedPlatforms]);

  const hasTumblrSelected = selectedTumblrAccounts.length > 0;

  const tiktokDeclaration = useMemo(() => {
    if (!hasTikTokSelected) return null;

    const musicLink = (
      <a
        href="https://www.tiktok.com/legal/page/global/music-usage-confirmation/en"
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary hover:underline inline-flex items-center gap-0.5"
      >
        Music Usage Confirmation
        <ExternalLink className="h-2 w-2" />
      </a>
    );

    const bcPolicyLink = (
      <a
        href="https://www.tiktok.com/legal/page/global/bc-policy/en"
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary hover:underline inline-flex items-center gap-0.5"
      >
        Branded Content Policy
        <ExternalLink className="h-2 w-2" />
      </a>
    );

    if (tiktokContentDisclosure && tiktokBrandedContent) {
      return (
        <span>
          By posting, you agree to TikTok's {bcPolicyLink} and {musicLink}.
        </span>
      );
    }
    return (
      <span>
        By posting, you agree to TikTok's {musicLink}.
      </span>
    );
  }, [hasTikTokSelected, tiktokContentDisclosure, tiktokBrandedContent]);

  // Reply states
  const [replyingComment, setReplyingComment] = useState<any>(null);
  const [replyText, setReplyText] = useState<string>('');
  const [replyWithLike, setReplyWithLike] = useState<boolean>(true);
  const [replyImage, setReplyImage] = useState<File | null>(null);
  const [replyImagePreview, setReplyImagePreview] = useState<string | null>(null);
  const [isSendingReply, setIsSendingReply] = useState<boolean>(false);
  const [likingCommentId, setLikingCommentId] = useState<string | null>(null);
  const replyImageInputRef = useRef<HTMLInputElement>(null);

  const handleReplyImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setReplyImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setReplyImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSendReply = async () => {
    if (!replyingComment || !replyText) return;

    setIsSendingReply(true);
    const formData = new FormData();
    formData.append('text', replyText);
    formData.append('like', replyWithLike.toString());
    if (replyImage) {
      formData.append('media', replyImage);
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/posts/comments/${replyingComment.id}/reply`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success("Reply sent successfully!");
      setReplyingComment(null);
      setReplyText('');
      setReplyWithLike(true);
      setReplyImage(null);
      setReplyImagePreview(null);

      // Update local state to mark as replied (and liked if selected)
      if (currentPost) {
        const updatedSchedules = currentPost.schedules?.map(s => ({
          ...s,
          commentsList: s.commentsList?.map(c =>
            c.id === replyingComment.id ? { ...c, isReplied: true, isLiked: replyWithLike ? true : c.isLiked } : c
          )
        }));
        setCurrentPost({ ...currentPost, schedules: updatedSchedules });
      }
    } catch (err: any) {
      console.error("Error sending reply:", err);
      toast.error(err.response?.data?.error || "Failed to send reply");
    } finally {
      setIsSendingReply(false);
    }
  };

  const handleLikeComment = async (commentId: string) => {
    if (likingCommentId) return;
    setLikingCommentId(commentId);

    try {
      await axios.post(
        `${API_BASE_URL}/posts/comments/${commentId}/like`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Liked!");

      // Update local state to mark as liked
      if (currentPost) {
        const updatedSchedules = currentPost.schedules?.map(s => ({
          ...s,
          commentsList: s.commentsList?.map(c =>
            c.id === commentId ? { ...c, isLiked: true } : c
          )
        }));
        setCurrentPost({ ...currentPost, schedules: updatedSchedules });
      }
    } catch (err: any) {
      console.error("Error liking comment:", err);
      toast.error(err.response?.data?.error || "Failed to like");
    } finally {
      setLikingCommentId(null);
    }
  };

  // Initialize evergreen tags from user data
  useEffect(() => {
    if (user?.evergreenTags) {
      try {
        const tags = Array.isArray(user.evergreenTags)
          ? user.evergreenTags
          : (typeof user.evergreenTags === 'string' ? JSON.parse(user.evergreenTags) : []);
        setEvergreenTags(Array.isArray(tags) ? tags : []);
      } catch (e) {
        console.error("Error parsing evergreen tags:", e);
        setEvergreenTags([]);
      }
    } else {
      setEvergreenTags([]);
    }
  }, [user?.id, user?.evergreenTags]);

  // Initialize inactive tags from currentPost if available
  useEffect(() => {
    if (currentPost?.config && (currentPost.config as any).inactiveTags) {
      setInactiveTags((currentPost.config as any).inactiveTags);
    }
  }, [currentPost]);

  // Sync evergreen tags to DB
  const saveEvergreenTagsToDB = async (tags: string[]) => {
    if (!token) return;
    try {
      const response = await axios.put(`${API_BASE_URL}/users/evergreen-tags`, { evergreenTags: tags }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Update global user state with new tags
      if (user) {
        setUser({ ...user, evergreenTags: response.data.evergreenTags });
      }
    } catch (error) {
      console.error('Error saving evergreen tags to DB:', error);
    }
  };

  const toggleTagActive = (tag: string) => {
    if (inactiveTags.includes(tag)) {
      setInactiveTags(inactiveTags.filter(t => t !== tag));
    } else {
      setInactiveTags([...inactiveTags, tag]);
    }
  };

  const [uploading, setUploading] = useState<boolean>(false);
  const [isPublishing, setIsPublishing] = useState<boolean>(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [showDeleteLinks, setShowDeleteLinks] = useState<boolean>(false);
  const [progress, setProgress] = useState<UploadProgress>({ progress: 0, status: 'idle', message: '' });
  const [error, setError] = useState<string>('');
  const [warning, setWarning] = useState<string>('');
  const [videoStartTime, setVideoStartTime] = useState<number | null>(null);
  const [videoEndTime, setVideoEndTime] = useState<number | null>(null);
  // localFile is now derived from mediaItems[0] (see below)
  const [videoDuration, setVideoDuration] = useState<number | null>(null);
  const [fileSize, setFileSize] = useState<number | null>(null);
  // Auto-derive fileSize from mediaItems (covers all upload paths: drag/drop, file picker, restore)
  useEffect(() => {
    const firstVideo = mediaItems.find(item => item.type === 'video');
    if (firstVideo?.localFile) {
      setFileSize(firstVideo.localFile.size);
    }
    // Don't reset to null here — DB-restored fileSize (without localFile) should persist
  }, [mediaItems]);
  const [convertedVideoPath, setConvertedVideoPath] = useState<string | null>(null);
  const [hasValidationErrors, setHasValidationErrors] = useState<boolean>(false);
  const [showUploadProgressDialog, setShowUploadProgressDialog] = useState<boolean>(false);

  const platformStatuses = useMemo(() => {
    return selectedPlatforms.map(id => {
      const sa = activeProject?.socialAccounts?.find(a => a.socialAccount.id === id);
      const status = progress.platformStatus?.[id] || { status: 'pending' as const };
      return {
        platformId: id,
        platformName: sa?.socialAccount.name || 'Account',
        platformType: sa?.socialAccount.platform || 'unknown',
        status: status.status,
        errorMessage: status.errorMessage
      };
    });
  }, [selectedPlatforms, activeProject, progress.platformStatus]);

  const [showCropper, setShowCropper] = useState<boolean>(false);
  const [captions, setCaptions] = useState<any[]>([]);

  // Dual Video Configuration (Desktop & Mobile)
  const [activeTab, setActiveTab] = useState<'desktop' | 'mobile'>('desktop');

  const [desktopConfig, setDesktopConfig] = useState<{
    cropData: any;
    crop: { x: number; y: number } | null;
    zoom: number;
  }>({
    cropData: null,
    crop: null,
    zoom: 1
  });

  const [mobileConfig, setMobileConfig] = useState<{
    cropData: any;
    crop: { x: number; y: number } | null;
    zoom: number;
  }>({
    cropData: null,
    crop: null,
    zoom: 1
  });

  const [unifyFormat, setUnifyFormat] = useState<boolean>(true);

  const hasVerticalPlatforms = useMemo(() => {
    return selectedPlatforms.some(id => {
      const sa = activeProject?.socialAccounts?.find(a => a.socialAccount.id === id);
      const platform = sa?.socialAccount.platform.toLowerCase() || '';
      if (platform === 'youtube' && youtubeVideoType === 'short') return true;
      return ['tiktok', 'instagram'].includes(platform);
    });
  }, [selectedPlatforms, activeProject, youtubeVideoType]);

  const hasHorizontalPlatforms = useMemo(() => {
    return selectedPlatforms.some(id => {
      const sa = activeProject?.socialAccounts?.find(a => a.socialAccount.id === id);
      const platform = sa?.socialAccount.platform.toLowerCase() || '';
      if (platform === 'youtube' && youtubeVideoType === 'video') return true;
      return !['tiktok', 'instagram'].includes(platform);
    });
  }, [selectedPlatforms, activeProject, youtubeVideoType]);

  useEffect(() => {
    if (hasHorizontalPlatforms && !hasVerticalPlatforms) {
      setActiveTab('desktop');
    } else if (hasVerticalPlatforms && !hasHorizontalPlatforms) {
      setActiveTab('mobile');
    }
  }, [hasHorizontalPlatforms, hasVerticalPlatforms]);

  const currentTabConfig = activeTab === 'desktop' ? desktopConfig : mobileConfig;
  const setCurrentTabConfig = (updates: any) => {
    if (activeTab === 'desktop') {
      setDesktopConfig(prev => ({ ...prev, ...updates }));
    } else {
      setMobileConfig(prev => ({ ...prev, ...updates }));
    }
    // Sync crop data to the first media item's videoConfig so MediaPreview/VideoPlayer can show the overlay
    if (updates.cropData !== undefined) {
      setMediaItems(prev => {
        if (prev.length === 0) return prev;
        const configKey = activeTab === 'desktop' ? 'desktopConfig' : 'mobileConfig';
        const first = prev[0];
        if (!first.videoConfig) return prev;
        return [
          {
            ...first,
            videoConfig: {
              ...first.videoConfig,
              [configKey]: {
                ...(first.videoConfig as any)[configKey],
                cropData: updates.cropData,
                crop: updates.crop,
                zoom: updates.zoom,
              },
            },
          },
          ...prev.slice(1),
        ];
      });
    }
  };

  // Derive legacy values from mediaItems for backward compatibility
  const firstMediaItem = mediaItems.length > 0 ? mediaItems[0] : null;
  const mediaPath = firstMediaItem?.path || '';
  const mediaPreview = firstMediaItem?.preview || '';
  const localFile = firstMediaItem?.localFile || null;

  // Helper: set the first media item (replaces legacy setMediaPath/setMediaPreview/setLocalFile)
  const setMediaAsSingleItem = useCallback((path: string, preview: string, file: File | null = null) => {
    if (!path && !preview && !file) {
      setMediaItems([]);
      return;
    }
    const isVideo = path === 'local-video' || !!path?.match(/\.(mp4|mov|avi|mkv|webm|m4v)$/i);
    const item = createMediaItem(
      isVideo ? 'video' : 'image',
      file || undefined,
      path,
      preview
    );
    setMediaItems([item]);
  }, []);

  // Note: Auto-save draft removed. Drafts are only used when returning from social connection flow.

  // Warn before closing tab while uploading
  useEffect(() => {
    if (!uploading) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [uploading]);

  const [loadingTrend, setLoadingTrend] = useState<boolean>(false);
  const [trendData, setTrendData] = useState<any>(null);
  const [quoteTweetId, setQuoteTweetId] = useState<string | null>(null);
  const [recommendingVideo, setRecommendingVideo] = useState<boolean>(false);
  const [generatingCaptions, setGeneratingCaptions] = useState<boolean>(false);

  const fetchTrendData = async () => {
    if (!trendId) return;
    setLoadingTrend(true);
    try {
      // We need an endpoint to get trend by ID. 
      // Let's assume there is one or we can filter from all trends.
      const response = await axios.get(`${API_BASE_URL}/social/trends`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const trends = response.data.masterTrends;
      const currentTrend = trends.find((t: any) => t.id === trendId);
      if (currentTrend) {
        setTrendData(currentTrend);
      }
    } catch (err) {
      console.error('Error fetching trend data:', err);
    } finally {
      setLoadingTrend(false);
    }
  };

  useEffect(() => {
    if (trendId && !trendData && !loadingTrend) {
      fetchTrendData();
    }
  }, [trendId, trendData, loadingTrend]);
  const [refreshingStats, setRefreshingStats] = useState<boolean>(false);
  const [resendingScheduleId, setResendingScheduleId] = useState<string | null>(null);
  const [showGenerateDialog, setShowGenerateDialog] = useState<boolean>(false);
  const [generateProgress, setGenerateProgress] = useState({ progress: 0, message: '' });
  const [shouldHighlightSocials, setShouldHighlightSocials] = useState<boolean>(false);
  const [isTargetDialogOpen, setIsTargetDialogOpen] = useState<boolean>(false);
  const [hasRecommendationAvailable, setHasRecommendationAvailable] = useState<boolean>(false);
  const [userSelectedCategory, setUserSelectedCategory] = useState<string | null>(null);

  // Check if a recommendation is available when modal opens for a mission
  useEffect(() => {
    if (isOpen && currentPost?.type && !mediaPath && !localFile) {
      const checkRecommendation = async () => {
        try {
          const response = await axios.get(`${API_BASE_URL}/recommendations/recommend-media`, {
            params: {
              missionType: currentPost?.type,
              pillarId: currentPost?.metadata?.pillarId,
              missionTitle: currentPost?.title
            },
            headers: { Authorization: `Bearer ${token}` }
          });
          setHasRecommendationAvailable(!!(response.data && !Array.isArray(response.data) && response.data.filePath));
        } catch (err) {
          setHasRecommendationAvailable(false);
        }
      };
      checkRecommendation();
    } else {
      setHasRecommendationAvailable(false);
    }
  }, [isOpen, currentPost?.type, mediaPath, localFile]);

  const uniqueEvidences = React.useMemo(() => {
    if (!trendData?.evidences) return [];
    const map = new Map<string, any>();
    trendData.evidences.forEach((ev: any) => {
      const handle = ev.authorHandle || ev.url?.split('x.com/')[1]?.split('/')[0] || 'unknown';
      const existing = map.get(handle);
      if (!existing || (ev.likeCount || 0) > (existing.likeCount || 0)) {
        map.set(handle, ev);
      }
    });
    return Array.from(map.values());
  }, [trendData?.evidences]);

  useEffect(() => {
    if (trendData?.evidences?.length > 0 && !quoteTweetId) {
      const sorted = [...trendData.evidences].sort((a: any, b: any) =>
        ((b.likeCount || 0) + (b.retweetCount || 0)) - ((a.likeCount || 0) + (a.retweetCount || 0))
      );
      const tweetUrl = sorted[0].url;
      const tweetId = tweetUrl.split('/').pop();
      setQuoteTweetId(tweetId);
    }
  }, [trendData, quoteTweetId]);

  const [lastInitializedPostId, setLastInitializedPostId] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setLastInitializedPostId(null);
      return;
    }

    // Only initialize once per unique post/mission/trend
    const currentId = currentPost?.id ||
      (currentPost?.trendId ? `new-trend-${currentPost.trendId}` :
        (currentPost?.tags?.length ? `new-tag-${currentPost.tags[0]}` :
          (currentPost?.title ? `new-mission-${currentPost.title}` : 'new-post')));
    if (lastInitializedPostId === currentId) return;

    // Reset trend-related state when opening a new post/mission
    setTrendData(null);
    setQuoteTweetId(null);

    const cleanText = (text: string) => {
      return (text || '')
        .split(/\s+/)
        .filter(word => !word.startsWith('#'))
        .join(' ')
        .trim();
    };

    if (isReadMode && currentPost) {
      // Auto-select the only schedule if there's only one
      if (currentPost.schedules && currentPost.schedules.length === 1) {
        setSelectedScheduleId(currentPost.schedules[0].id);
      } else if (currentPost.schedules && currentPost.schedules.length > 0) {
        setSelectedScheduleId('all');
      } else {
        setSelectedScheduleId('all');
      }

      // For published posts, the media path is inside the media array
      const firstMedia = currentPost.media && (currentPost as any).media.length > 0
        ? (currentPost as any).media[0].filePath
        : (currentPost.mediaPath || '');

      // Initialize translations
      if (currentPost.translations) {
        setTranslations(currentPost.translations);
      } else {
        const legacyTrans: any = {};
        if (currentPost.textEn || currentPost.initialTextEn) {
          legacyTrans.en = { text: currentPost.textEn || currentPost.initialTextEn || '' };
        }
        if (currentPost.textEs || currentPost.initialTextEs) {
          legacyTrans.es = { text: currentPost.textEs || currentPost.initialTextEs || '' };
        }
        setTranslations(legacyTrans);
      }

      const firstTrans = Object.keys(currentPost.translations || {}).length > 0
        ? Object.keys(currentPost.translations || {})[0]
        : (currentPost.textEn || currentPost.initialTextEn || (currentPost.translations as any)?.en ? 'en' : 'es');
      if (firstTrans) setBaseLanguage(firstTrans);

      const firstThumbnail = currentPost.media && (currentPost as any).media.length > 0
        ? (currentPost as any).media[0].thumbnailPath
        : (currentPost.mediaPreview || '');

      // Build mediaItems from the post's media array
      if (currentPost.media && (currentPost as any).media.length > 0) {
        const items: MediaItem[] = (currentPost as any).media
          .sort((a: any, b: any) => (a.order || 0) - (b.order || 0)) // Sort by order
          .map((m: any, idx: number) => {
            const isVideo = !!m.filePath?.match(/\.(mp4|mov|avi|mkv|webm|m4v)$/i);
            const preview = m.thumbnailPath || `${API_BASE_URL}/social/media?path=${encodeURIComponent(m.filePath)}&token=${token}`;
            const item = createMediaItem(isVideo ? 'video' : 'image', undefined, m.filePath, preview);
            item.id = m.id || item.id; // Preserve DB id if available
            item.order = m.order ?? idx;
            if (isVideo && m.thumbnailPath) {
              item.thumbnailUrl = `${API_BASE_URL}/social/media?path=${encodeURIComponent(m.thumbnailPath)}&token=${token}`;
            }
            // Restore crop/zoom config from post config so the preview overlay shows
            if (isVideo && currentPost?.config) {
              const postConfig = currentPost.config as any;
              if (item.videoConfig) {
                item.videoConfig = {
                  ...item.videoConfig,
                  desktopConfig: postConfig.desktopConfig || item.videoConfig.desktopConfig,
                  mobileConfig: postConfig.mobileConfig || item.videoConfig.mobileConfig,
                };
              }
            }
            return item;
          });
        setMediaItems(items);
        // Restore fileSize from DB media for platform validation
        const firstVideoMedia = (currentPost as any).media.find((m: any) => m.filePath?.match(/\.(mp4|mov|avi|mkv|webm|m4v)$/i));
        if (firstVideoMedia?.fileSize) {
          setFileSize(Number(firstVideoMedia.fileSize));
        }
      } else {
        setMediaAsSingleItem(firstMedia, firstThumbnail || '');
      }
      setSelectedTags(currentPost.campaignTags || currentPost.tags || []);
      setEvergreenTags(currentPost.evergreenTags || []);
      setPostMode('schedule');
      if (currentPost.scheduledAt) {
        setScheduledAt(moment(currentPost.scheduledAt).format('YYYY-MM-DDTHH:mm'));
      }
    } else {
      // EDIT MODE or NEW POST
      console.log('[PostModal] Initializing in EDIT/NEW MODE', currentPost);
      const restoredLocalFile = currentPost?.localFile || null;

      const missionMedia = currentPost?.media && currentPost.media.length > 0
        ? currentPost.media[0]
        : null;

      const missionMediaPath = missionMedia?.filePath || '';
      const finalMediaPath = currentPost?.mediaPath || missionMediaPath;

      // Initialize translations
      if (currentPost?.translations) {
        setTranslations(currentPost.translations);
      } else {
        const legacyTrans: any = {};
        if (currentPost?.textEn || currentPost?.initialTextEn) {
          legacyTrans.en = { text: currentPost.textEn || cleanText(currentPost.initialTextEn || '') };
        }
        if (currentPost?.textEs || currentPost?.initialTextEs) {
          legacyTrans.es = { text: currentPost.textEs || cleanText(currentPost.initialTextEs || '') };
        }
        setTranslations(legacyTrans);
      }

      const firstTrans = Object.keys(currentPost?.translations || {}).length > 0
        ? Object.keys(currentPost?.translations || {})[0]
        : (currentPost?.textEn || currentPost?.initialTextEn || 'en');
      if (firstTrans) setBaseLanguage(firstTrans);

      const missionMediaPreview = missionMedia
        ? `${API_BASE_URL}/social/media?path=${encodeURIComponent(missionMedia.filePath)}&token=${token}`
        : '';
      const finalMediaPreview = currentPost?.mediaPreview || missionMediaPreview;

      // Restore mediaItems from draft or build from post.media or single media
      const draftMediaItems = (currentPost as any)?.mediaItems;
      const postMedia = currentPost?.media;
      
      if (draftMediaItems && draftMediaItems.length > 0) {
        // Restore from draft that had multi-media
        console.log('[PostModal] Restoring', draftMediaItems.length, 'media items from draft');
        setMediaItems(draftMediaItems.map((item: any, idx: number) => ({
          ...item,
          localFile: null, // File objects can't be persisted
          order: idx,
        })));
      } else if (postMedia && postMedia.length > 1) {
        // Post has multiple media items (not a draft, but a published/scheduled post with carousel)
        const items: MediaItem[] = postMedia
          .sort((a: any, b: any) => (a.order || 0) - (b.order || 0))
          .map((m: any, idx: number) => {
            const isVideo = !!m.filePath?.match(/\.(mp4|mov|avi|mkv|webm|m4v)$/i);
            const preview = m.thumbnailPath || `${API_BASE_URL}/social/media?path=${encodeURIComponent(m.filePath)}&token=${token}`;
            const item = createMediaItem(isVideo ? 'video' : 'image', undefined, m.filePath, preview);
            item.id = m.id || item.id;
            item.order = m.order ?? idx;
            if (isVideo && m.thumbnailPath) {
              item.thumbnailUrl = `${API_BASE_URL}/social/media?path=${encodeURIComponent(m.thumbnailPath)}&token=${token}`;
            }
            return item;
          });
        console.log('[PostModal] Restored', items.length, 'media items from post.media:', items);
        setMediaItems(items);
      } else {
        setMediaAsSingleItem(finalMediaPath, finalMediaPreview, restoredLocalFile);
      }

      const campaignTags = currentPost?.campaignTags || currentPost?.tags || [];
      setSelectedTags(campaignTags);

      const userEvergreen = user?.evergreenTags || [];
      const initialEvergreen = currentPost?.evergreenTags || userEvergreen;
      const lowerCampaignTags = campaignTags.map(t => t.toLowerCase());
      const filteredEvergreen = initialEvergreen.filter(tag => !lowerCampaignTags.includes(tag.toLowerCase()));

      setEvergreenTags(filteredEvergreen);
      setPostMode(currentPost?.scheduledAt ? 'schedule' : 'now');
      setScheduledAt(currentPost?.scheduledAt || moment().add(1, 'hour').format('YYYY-MM-DDTHH:mm'));
      setTrendId(currentPost?.trendId || null);
      // Restore fileSize: prefer local File object, fallback to DB media fileSize
      const dbVideoMedia = (currentPost?.media as any[])?.find((m: any) => m.filePath?.match(/\.(mp4|mov|avi|mkv|webm|m4v)$/i));
      setFileSize(restoredLocalFile?.size || (dbVideoMedia?.fileSize ? Number(dbVideoMedia.fileSize) : null));
      setVideoDuration(currentPost?.videoDuration || null);
      setVideoStartTime(currentPost?.videoStartTime || null);
      setVideoEndTime(currentPost?.videoEndTime || null);
      setCaptions(currentPost?.captions || []);
      setQuoteTweetId(currentPost?.config?.quoteTweetId || null);
      setDesktopConfig(currentPost?.config?.desktopConfig || { cropData: null, crop: null, zoom: 1 });
      setMobileConfig(currentPost?.config?.mobileConfig || { cropData: null, crop: null, zoom: 1 });
      setUnifyFormat(currentPost?.config?.unifyFormat ?? user?.unifyFormat ?? false);

      setConvertedVideoPath(null);
      setError('');
      setWarning('');
    }

    setLastInitializedPostId(currentId);

    // TikTok/YouTube/Tumblr Specific Initialization
    const tiktokMeta = currentPost?.tiktokMetadata || currentPost?.config?.tiktokMetadata;
    setTiktokTitle(tiktokMeta?.title || '');
    setTiktokPrivacy(tiktokMeta?.privacyLevel || '');
    setTiktokAllowComments(tiktokMeta?.allowComments ?? false);
    setTiktokAllowDuet(tiktokMeta?.allowDuet ?? false);
    setTiktokAllowStitch(tiktokMeta?.allowStitch ?? false);
    setTiktokContentDisclosure(tiktokMeta?.contentDisclosure ?? false);
    setTiktokYourBrand(tiktokMeta?.yourBrand ?? false);
    setTiktokBrandedContent(tiktokMeta?.brandedContent ?? false);

    const youtubeMeta = currentPost?.youtubeMetadata || currentPost?.config?.youtubeMetadata;
    setYoutubeVideoType(youtubeMeta?.videoType || user?.youtubeDefaults?.videoType || 'short');

    const tumblrMeta = currentPost?.tumblrMetadata || currentPost?.config?.tumblrMetadata;
    const tumblrDefaults = (user as any)?.tumblrDefaults;
    setTumblrContentDisclosure(tumblrMeta?.contentDisclosure ?? tumblrDefaults?.contentDisclosure ?? false);
    setTumblrYourBrand(tumblrMeta?.yourBrand ?? tumblrDefaults?.yourBrand ?? false);
    setTumblrBrandedContent(tumblrMeta?.brandedContent ?? tumblrDefaults?.brandedContent ?? false);
  }, [isOpen, isReadMode, currentPost, token, user, lastInitializedPostId]);

  const handleArchivePost = () => {
    if (!currentPost?.id) return;
    setShowDeleteConfirm(true);
    setShowDeleteLinks(false);
  };

  const confirmArchivePost = async () => {
    if (!currentPost?.id) return;

    try {
      setIsPublishing(true); // Reuse publishing state for loading
      await axios.delete(`${API_BASE_URL}/posts/${currentPost.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Post deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      queryClient.invalidateQueries({ queryKey: ['all-posts-table'] });
      queryClient.invalidateQueries({ queryKey: ['weekly-summary'] });
      setShowDeleteConfirm(false);
      closePostModal();
    } catch (err: any) {
      console.error('Error deleting post:', err);
      toast.error(err.response?.data?.error || 'Failed to delete post');
    } finally {
      setIsPublishing(false);
    }
  };

  const handleClose = () => {
    if (uploading) return;
    closePostModal();
  };

  const handleFileChange = (file: File | null) => {
    if (file) {
      setFileSize(file.size);
      // Update the first media item's localFile
      if (mediaItems.length > 0) {
        setMediaItems(prev => prev.map((item, idx) => idx === 0 ? { ...item, localFile: file } : item));
      } else {
        const isVideo = file.type.startsWith('video/');
        const preview = URL.createObjectURL(file);
        const item = createMediaItem(isVideo ? 'video' : 'image', file, isVideo ? 'local-video' : '', preview);
        setMediaItems([item]);
      }
    } else {
      setFileSize(null);
    }
  };

  const handleAddMoreMedia = async (files: File[]) => {
    if (uploading) return;
    
    const newItems: MediaItem[] = [];

    for (const file of files) {
      const isVideo = file.type.startsWith('video/');
      const isImage = file.type.startsWith('image/');

      if (!isVideo && !isImage) {
        toast.error(`Unsupported file type: ${file.name}`);
        continue;
      }

      const preview = URL.createObjectURL(file);
      
      let thumbnailUrl = preview;
      if (isVideo) {
        try {
          thumbnailUrl = await generateVideoThumbnail(preview);
        } catch (err) {
          console.error('Failed to generate thumbnail:', err);
        }
      }

      const item = createMediaItem(
        isVideo ? 'video' : 'image',
        file,
        isVideo ? 'local-video' : '',
        preview
      );
      item.thumbnailUrl = thumbnailUrl;
      item.order = mediaItems.length + newItems.length;

      newItems.push(item);
    }

    if (newItems.length > 0) {
      setMediaItems(prev => [...prev, ...newItems]);
      toast.success(`Added ${newItems.length} media item(s)`);
    }
  };

  const handleDeleteVideo = () => {
    setMediaItems([]);
    setVideoStartTime(null);
    setVideoEndTime(null);
    setVideoDuration(null);
    setFileSize(null);
    setConvertedVideoPath(null);
    setError('');
    setWarning('');
    setGeneratingCaptions(false);
    setDesktopConfig({ cropData: null, crop: null, zoom: 1 });
    setMobileConfig({ cropData: null, crop: null, zoom: 1 });
  };

  const handleAddTag = (tag: string, isEvergreen: boolean = false) => {
    if (!tag.trim()) return;
    const normalizedTag = `#${tag.trim().replace(/^#+/, '')}`;
    if (isEvergreen) {
      const isAlreadyInEvergreen = evergreenTags.some(t => t.toLowerCase() === normalizedTag.toLowerCase());
      if (!isAlreadyInEvergreen) {
        const newTags = [...evergreenTags, normalizedTag];
        setEvergreenTags(newTags);
        saveEvergreenTagsToDB(newTags);

        // If it was in campaign tags, remove it from there
        setSelectedTags(selectedTags.filter(t => t.toLowerCase() !== normalizedTag.toLowerCase()));
      }
    } else {
      const isAlreadyInCampaign = selectedTags.some(t => t.toLowerCase() === normalizedTag.toLowerCase());
      if (!isAlreadyInCampaign) {
        setSelectedTags([...selectedTags, normalizedTag]);

        // If it was in evergreen tags, remove it from there
        setEvergreenTags(evergreenTags.filter(t => t.toLowerCase() !== normalizedTag.toLowerCase()));
      }
    }
  };

  const handleRecommendVideo = async () => {
    setRecommendingVideo(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/recommendations/recommend-media`, {
        params: {
          missionType: currentPost?.type,
          pillarId: currentPost?.metadata?.pillarId,
          missionTitle: currentPost?.title
        },
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data && response.data.filePath) {
        const recommendedMedia = response.data;
        console.log('[PostModal] Recommended media received:', recommendedMedia);

        // Reset local video states before applying recommended one
        setFileSize(null);
        setConvertedVideoPath(null);
        setVideoStartTime(null);
        setVideoEndTime(null);
        setVideoDuration(null);
        setDesktopConfig({ cropData: null, crop: null, zoom: 1 });
        setMobileConfig({ cropData: null, crop: null, zoom: 1 });

        // Set path and preview via mediaItems
        const filePath = recommendedMedia.filePath;
        const previewUrl = recommendedMedia.thumbnailPath || `${API_BASE_URL}/social/media?path=${encodeURIComponent(filePath)}&token=${token}`;

        setMediaAsSingleItem(filePath, previewUrl);

        // Update texts if AI suggested them
        const aiMeta = recommendedMedia.aiMetadata;
        if (aiMeta && aiMeta.suggestedTexts) {
          console.log('[PostModal] AI suggested texts found:', aiMeta.suggestedTexts);
          if (aiMeta.suggestedTexts.en?.[0]) {
            const newTrans = { ...translations };
            newTrans.en = { ...newTrans.en, text: aiMeta.suggestedTexts.en[0] };
            setTranslations(newTrans);
          }
          if (aiMeta.suggestedTexts.es?.[0]) {
            const newTrans = { ...translations };
            newTrans.es = { ...newTrans.es, text: aiMeta.suggestedTexts.es[0] };
            setTranslations(newTrans);
          }
        }

        toast.success('Found a matching video for this mission!');
      } else {
        toast.info('No matching videos found in your gallery.');
      }
    } catch (err) {
      console.error('Error recommending video:', err);
      toast.error('Failed to get a recommendation');
    } finally {
      setRecommendingVideo(false);
    }
  };

  const handleRemoveTag = (tagToRemove: string, isEvergreen: boolean = false) => {
    if (isEvergreen) {
      const newTags = evergreenTags.filter(tag => tag !== tagToRemove);
      setEvergreenTags(newTags);
      saveEvergreenTagsToDB(newTags);
    } else {
      setSelectedTags(selectedTags.filter(tag => tag !== tagToRemove));
    }
  };

  const handleTagInputKeyPress = (e: React.KeyboardEvent<HTMLInputElement>, isEvergreen: boolean = false) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const input = e.currentTarget;
      if (input && input.value.trim()) {
        handleAddTag(input.value.trim(), isEvergreen);
        input.value = '';
      }
    }
  };

  const getMinCharacterLimit = (): number | undefined => {
    if (selectedPlatforms.length === 0) return undefined;
    const limits = selectedPlatforms
      .map(id => getPlatformRequirement(id)?.maxCharacters)
      .filter((l): l is number => l !== undefined);
    return limits.length > 0 ? Math.min(...limits) : undefined;
  };

  const minCharacterLimit = getMinCharacterLimit();
  const hasSpanishPlatform = selectedPlatforms.includes('x-es');

  const handleGenerateCaptions = async () => {
    if (!mediaPath && !localFile) return;
    setGeneratingCaptions(true);
    setShowGenerateDialog(true);
    setGenerateProgress({ progress: 0, message: 'Initializing...' });
    try {
      let finalMediaPath = mediaPath;
      if (localFile && (!mediaPath || mediaPath === 'local-video')) {
        const formData = new FormData();
        formData.append('media', localFile);
        const uploadRes = await axios.post(`${API_BASE_URL}/social/upload-file`, formData, {
          headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` }
        });
        finalMediaPath = uploadRes.data.filePath;
      }
      const response = await axios.post(`${API_BASE_URL}/social/generate-captions`, {
        mediaPath: finalMediaPath,
        gameId: activeProject?.id,
      }, { headers: { 'Authorization': `Bearer ${token}` } });

      const { sessionId } = response.data;
      const poll = setInterval(async () => {
        const res = await axios.get(`${API_BASE_URL}/social/generate-captions-status/${sessionId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setGenerateProgress({ progress: res.data.progress, message: res.data.message });
        if (res.data.status === 'completed') {
          clearInterval(poll);
          setGeneratingCaptions(false);
          setShowGenerateDialog(false);
          if (res.data.result) {
            const newTrans = { ...translations };
            if (res.data.result.captions.length > 0) {
              newTrans.en = { ...newTrans.en, text: res.data.result.captions[0] };
            }
            if (res.data.result.captionsEs.length > 0) {
              newTrans.es = { ...newTrans.es, text: res.data.result.captionsEs[0] };
            }
            setTranslations(newTrans);
            toast.success("Captions generated!");
          }
        } else if (res.data.status === 'error') {
          clearInterval(poll);
          setGeneratingCaptions(false);
          setShowGenerateDialog(false);
          setError(res.data.message);
          toast.error("Failed to generate captions");
        }
      }, 2000);
    } catch (err: any) {
      setGeneratingCaptions(false);
      setShowGenerateDialog(false);
      setError(err.message);
    }
  };

  const handleRefreshStats = async () => {
    if (!currentPost?.id || refreshingStats) return;

    setRefreshingStats(true);
    try {
      await axios.post(`${API_BASE_URL}/analytics/posts/${currentPost.id}/update`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Engagement data updated!");
      // Refresh the post data
      openPostById(currentPost.id);
    } catch (err: any) {
      toast.error(err.response?.data?.error || "Failed to update stats");
    } finally {
      setRefreshingStats(false);
    }
  };

  const handleResend = async (scheduleId: string) => {
    if (resendingScheduleId) return;
    setResendingScheduleId(scheduleId);
    try {
      const response = await axios.post(`${API_BASE_URL}/posts/schedule/${scheduleId}/resend`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.status === 'COMPLETED') {
        toast.success('Post resent successfully!');
      } else if (response.data.status === 'FAILED') {
        toast.error(`Post failed again: ${response.data.result || 'Unknown error'}`);
      } else {
        toast.info(`Post is being processed: ${response.data.status}`);
      }

      // Refresh the post data to show new status/stats
      if (currentPost?.id) openPostById(currentPost.id);
    } catch (err: any) {
      toast.error(`Failed to resend: ${err.response?.data?.error || err.message}`);
    } finally {
      setResendingScheduleId(null);
    }
  };

  const isRefreshDisabled = () => {
    if (!currentPost?.schedules || currentPost.schedules.length === 0) return true;

    // Admins can always refresh
    if (user?.isAdmin) return false;

    // Find the latest update among all schedules
    const lastUpdate = currentPost.schedules.reduce((latest: Date | null, s) => {
      if (!s.lastStatsUpdate) return latest;
      const d = new Date(s.lastStatsUpdate);
      if (!latest || d > latest) return d;
      return latest;
    }, null);

    if (!lastUpdate) return false;

    // Check if it's been at least 1 hour (3600000 ms)
    const oneHourAgo = Date.now() - 3600000;
    return lastUpdate.getTime() > oneHourAgo;
  };

  const getLastUpdateText = () => {
    const lastUpdate = currentPost?.schedules?.reduce((latest: Date | null, s) => {
      if (!s.lastStatsUpdate) return latest;
      const d = new Date(s.lastStatsUpdate);
      if (!latest || d > latest) return d;
      return latest;
    }, null);

    if (!lastUpdate) return "Never updated";
    return moment(lastUpdate).fromNow();
  };

  const handleUpload = async () => {
    if (selectedPlatforms.length === 0) {
      toast.error('Please select at least one social media platform');
      return;
    }
    setUploading(true);
    setShowUploadProgressDialog(true);
    setIsPublishing(false);
    setProgress({ progress: 0, status: 'uploading', message: 'Preparing post...' });

    try {
      // Upload all media items that have local files
      const finalMediaItems: any[] = [];

      for (let i = 0; i < mediaItems.length; i++) {
        const item = mediaItems[i];
        let itemPath = item.path;

        // Upload if it's a local file that hasn't been uploaded yet
        if (item.localFile && (!itemPath || itemPath === 'local-video')) {
          const formData = new FormData();
          formData.append('media', item.localFile);
          const uploadRes = await axios.post(`${API_BASE_URL}/social/upload-file`, formData, {
            headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` },
            onUploadProgress: (progressEvent) => {
              if (progressEvent.total) {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                const totalSteps = (mediaItems.length * 2) + selectedPlatforms.length;
                const completedSteps = i * 2 + (percentCompleted / 100) * 2;
                const overallProgress = Math.round((completedSteps / totalSteps) * 100);

                setProgress(prev => ({
                  ...prev,
                  progress: overallProgress,
                  status: 'uploading',
                  message: `Uploading media ${i + 1}/${mediaItems.length} (${percentCompleted}%)...`
                }));
              }
            }
          });
          itemPath = uploadRes.data.filePath;
        }

        if (itemPath) {
          finalMediaItems.push({
            path: itemPath,
            type: item.type,
            order: item.order,
            videoConfig: item.videoConfig ? {
              startTime: item.videoConfig.startTime,
              endTime: item.videoConfig.endTime,
              captions: item.videoConfig.captions,
              cropData: item.videoConfig.desktopConfig?.cropData,
              crop: item.videoConfig.desktopConfig?.crop,
              zoom: item.videoConfig.desktopConfig?.zoom,
              desktopConfig: item.videoConfig.desktopConfig,
              mobileConfig: item.videoConfig.mobileConfig,
              unifyFormat: item.videoConfig.unifyFormat
            } : undefined
          });
        }
      }

      // Ensure all media items were uploaded; otherwise we would create a post with fewer files than the user added
      if (finalMediaItems.length < mediaItems.length) {
        toast.error(`Only ${finalMediaItems.length} of ${mediaItems.length} file(s) were ready. Please try again or re-add the missing file(s).`);
        setUploading(false);
        setShowUploadProgressDialog(false);
        return;
      }

      const extractHashtags = (text: string | null) => {
        if (!text) return [];
        const hashtagRegex = /#[a-zA-Z0-9_]+/g;
        return text.match(hashtagRegex) || [];
      };

      const allTexts = Object.values(translations).map(t => t.text);
      const tagsFromText = allTexts.flatMap(text => extractHashtags(text));

      // Merge tags from text into the tags we send to the DB
      // We use a Set to ensure unique tags (case-insensitive check)
      const lowerExistingTags = selectedTags.map(t => t.toLowerCase());
      const newTagsFromText = tagsFromText.filter(tag => !lowerExistingTags.includes(tag.toLowerCase()));

      // Remove duplicates from text itself too
      const uniqueNewTagsFromText: string[] = [];
      const seen = new Set<string>();
      for (const tag of newTagsFromText) {
        if (!seen.has(tag.toLowerCase())) {
          seen.add(tag.toLowerCase());
          uniqueNewTagsFromText.push(tag);
        }
      }

      const finalTagsForDB = [...selectedTags, ...uniqueNewTagsFromText];

      const activeEvergreenTags = evergreenTags.filter(t => !inactiveTags.includes(t));
      const activeCampaignTags = finalTagsForDB.filter(t => !inactiveTags.includes(t));

      const prepareFinalText = (text: string | null) => {
        if (!text) return null;
        const lowerText = text.toLowerCase();
        // Append tags that are active but NOT present in the text itself
        const allActiveTags = [...activeEvergreenTags, ...activeCampaignTags];
        const tagsToAppend = allActiveTags.filter(
          tag => !lowerText.includes(tag.toLowerCase())
        );
        return tagsToAppend.length > 0 ? `${text}\n\n${tagsToAppend.join(' ')}` : text;
      };

      const finalTranslations: any = {};
      Object.keys(translations).forEach(lang => {
        finalTranslations[lang] = {
          ...translations[lang],
          text: prepareFinalText(translations[lang].text)
        };
      });

      // Save TikTok defaults to user preferences if TikTok is selected
      if (hasTikTokSelected) {
        const newTiktokDefaults = {
          privacyLevel: tiktokPrivacy,
          allowComments: tiktokAllowComments,
          allowDuet: tiktokAllowDuet,
          allowStitch: tiktokAllowStitch,
          contentDisclosure: tiktokContentDisclosure,
          yourBrand: tiktokYourBrand,
          brandedContent: tiktokBrandedContent
        };

        axios.put(`${API_BASE_URL}/users/preferences`, {
          tiktokDefaults: newTiktokDefaults
        }, { headers: { 'Authorization': `Bearer ${token}` } })
          .then(res => {
            if (res.data.tiktokDefaults) {
              setUser({ ...user, tiktokDefaults: res.data.tiktokDefaults } as any);
            }
          })
          .catch(err => console.error("Error saving tiktok defaults:", err));
      }

      // Save YouTube defaults if YouTube is selected
      if (hasYouTubeSelected) {
        const newYoutubeDefaults = {
          videoType: youtubeVideoType
        };

        axios.put(`${API_BASE_URL}/users/preferences`, {
          youtubeDefaults: newYoutubeDefaults
        }, { headers: { 'Authorization': `Bearer ${token}` } })
          .then(res => {
            if (res.data.youtubeDefaults) {
              setUser({ ...user, youtubeDefaults: res.data.youtubeDefaults } as any);
            }
          })
          .catch(err => console.error("Error saving youtube defaults:", err));
      }

      // Save Tumblr defaults if Tumblr is selected
      if (hasTumblrSelected) {
        const newTumblrDefaults = {
          contentDisclosure: tumblrContentDisclosure,
          yourBrand: tumblrYourBrand,
          brandedContent: tumblrBrandedContent
        };

        axios.put(`${API_BASE_URL}/users/preferences`, {
          tumblrDefaults: newTumblrDefaults
        }, { headers: { 'Authorization': `Bearer ${token}` } })
          .then(res => {
            if (res.data.tumblrDefaults) {
              setUser({ ...user, tumblrDefaults: res.data.tumblrDefaults } as any);
            }
          })
          .catch(err => console.error("Error saving tumblr defaults:", err));
      }

      // Save unifyFormat to user preferences
      axios.put(`${API_BASE_URL}/users/preferences`, {
        unifyFormat: unifyFormat
      }, { headers: { 'Authorization': `Bearer ${token}` } })
        .then(res => {
          if (res.data.unifyFormat !== undefined) {
            setUser({ ...user, unifyFormat: res.data.unifyFormat } as any);
          }
        })
        .catch(err => console.error("Error saving unify format preference:", err));

      const response = await axios.post(`${API_BASE_URL}/social/upload`, {
        // Always send mediaItems when we have any media so server gets full array (fixes multi-file only first sent)
        mediaPath: finalMediaItems.length > 0 ? finalMediaItems[0].path : '',
        mediaItems: finalMediaItems.length > 0 ? finalMediaItems : undefined,
        translations: finalTranslations,
        gameId: activeProject?.id,
        selectedPlatforms,
        debugMode,
        postMode,
        scheduledAt: postMode === 'schedule' ? new Date(scheduledAt).toISOString() : new Date().toISOString(),
        tags: finalTagsForDB,
        evergreenTags: evergreenTags,
        inactiveTags: inactiveTags, // Save inactive state too
        unifyFormat,
        desktopConfig,
        mobileConfig,
        trendId: trendId,
        quoteTweetId: quoteTweetId,
        userCategory: userSelectedCategory,
        // TikTok specific metadata - use base title or first translation title
        tiktokMetadata: hasTikTokSelected ? {
          title: tiktokTitle || translations[baseLanguage]?.tiktokTitle || translations['en']?.tiktokTitle || '',
          privacyLevel: tiktokPrivacy,
          allowComments: tiktokAllowComments,
          allowDuet: tiktokAllowDuet,
          allowStitch: tiktokAllowStitch,
          contentDisclosure: tiktokContentDisclosure,
          yourBrand: tiktokYourBrand,
          brandedContent: tiktokBrandedContent
        } : undefined,
        // Tumblr specific metadata
        tumblrMetadata: hasTumblrSelected ? {
          contentDisclosure: tumblrContentDisclosure,
          yourBrand: tumblrYourBrand,
          brandedContent: tumblrBrandedContent
        } : undefined,
        // YouTube specific metadata
        youtubeMetadata: hasYouTubeSelected ? {
          videoType: youtubeVideoType
        } : undefined,
      }, { headers: { 'Authorization': `Bearer ${token}` } });

      const { sessionId, postId } = response.data;

      // SUCCESS! Server has the file.
      setIsPublishing(true);

      // Close dialog after 1s as requested
      setTimeout(() => {
        setShowUploadProgressDialog(false);
        closePostModal();
        setUploading(false);
        queryClient.invalidateQueries({ queryKey: ['posts'] });
        queryClient.invalidateQueries({ queryKey: ['scheduled-posts'] });
        queryClient.invalidateQueries({ queryKey: ['all-posts-table'] });
        queryClient.invalidateQueries({ queryKey: ['weekly-summary'] });
        refreshUser();
      }, 1000);

      // Background Progress Toast
      const es = new EventSource(`${API_BASE_URL}/social/progress/${sessionId}?token=${token}`);

      const toastId = toast.loading("Processing Publication...", {
        description: "Moving to background distribution...",
      });

      es.onmessage = (e) => {
        const data = JSON.parse(e.data);
        if (data.status === 'processing' || data.status === 'starting') {
          const platformCount = selectedPlatforms.length;
          const platformStatus = data.platformStatus || {};
          const completedCount = Object.values(platformStatus).filter((s: any) => s.status === 'completed' || s.status === 'error').length;

          const totalSteps = platformCount + 1;
          const completedSteps = completedCount + 1; // Backend process is already done
          const percent = Math.round((completedSteps / totalSteps) * 100);

          toast.loading(`Publishing: ${percent}%`, {
            id: toastId,
            description: data.message || "Processing social networks...",
          });
        } else if (data.status === 'completed') {
          toast.success("Post Published!", {
            id: toastId,
            description: "Check publication history for links.",
          });
          queryClient.invalidateQueries({ queryKey: ['posts'] });
          queryClient.invalidateQueries({ queryKey: ['weekly-summary'] });
          es.close();
        } else if (data.status === 'completed_with_errors') {
          toast.warning("Published with some errors", {
            id: toastId,
            description: data.message || "Some platforms failed to upload.",
          });
          queryClient.invalidateQueries({ queryKey: ['posts'] });
          queryClient.invalidateQueries({ queryKey: ['weekly-summary'] });
          es.close();
        } else if (data.status === 'scheduled') {
          toast.success("Post Scheduled!", {
            id: toastId,
            description: `Scheduled for publication.`,
          });
          queryClient.invalidateQueries({ queryKey: ['posts'] });
          queryClient.invalidateQueries({ queryKey: ['weekly-summary'] });
          es.close();
        } else if (data.status === 'error') {
          toast.error("Publishing Failed", {
            id: toastId,
            description: data.message || "An error occurred during publication.",
          });
          es.close();
        }
      };
    } catch (err: any) {
      setUploading(false);
      setIsPublishing(false);
      setShowUploadProgressDialog(false);
      toast.error(err.message || "Upload failed");
    }
  };

  const tiktokValidationErrors = useMemo(() => {
    if (!hasTikTokSelected) return false;
    if (!tiktokTitle.trim()) return true;
    if (!tiktokPrivacy) return true;
    if (tiktokContentDisclosure && !tiktokYourBrand && !tiktokBrandedContent) return true;

    const durationToCheck = ((videoEndTime ?? videoDuration) || 0) - (videoStartTime ?? 0);

    // Check duration against creator_info
    if (durationToCheck && durationToCheck > tiktokRestrictions.maxDuration) return true;

    // Check for API errors (like daily limit reached)
    if (selectedTikTokAccounts.some(account => {
      const infoResponse = tiktokCreatorInfo[account.socialAccount.id];
      return infoResponse?.error?.code && infoResponse.error.code !== 'ok';
    })) return true;

    return false;
  }, [hasTikTokSelected, tiktokTitle, tiktokPrivacy, tiktokContentDisclosure, tiktokYourBrand, tiktokBrandedContent, videoDuration, tiktokRestrictions.maxDuration, tiktokCreatorInfo, selectedTikTokAccounts]);

  const tumblrValidationErrors = useMemo(() => {
    if (!hasTumblrSelected) return false;
    if (tumblrContentDisclosure && !tumblrYourBrand && !tumblrBrandedContent) return true;
    return false;
  }, [hasTumblrSelected, tumblrContentDisclosure, tumblrYourBrand, tumblrBrandedContent]);

  const hasContent = useMemo(() => {
    return Object.values(translations).some(t => t.text && t.text.trim().length > 0);
  }, [translations]);

  // Check for blocking media issues (e.g., Instagram order-based restrictions)
  const mediaBlockingIssues = useMemo(() => {
    // Convert account IDs to platform names
    const platformNames = selectedPlatforms
      .map(accountId => {
        const account = activeProject?.socialAccounts?.find(sa => sa.socialAccount.id === accountId);
        return account?.socialAccount.platform;
      })
      .filter((platform): platform is string => platform !== undefined);
    
    return hasBlockingMediaIssues(
      mediaItems.map(item => ({
        type: item.type,
        path: item.path ?? undefined,
        fileName: item.localFile?.name ?? undefined,
      })),
      platformNames
    );
  }, [mediaItems, selectedPlatforms, activeProject]);

  const isUploadDisabled = uploading || isFetchingTiktokInfo || !hasContent || selectedPlatforms.length === 0 || hasValidationErrors || tiktokValidationErrors || tumblrValidationErrors || user?.tier === 'FREE' || mediaBlockingIssues.hasIssues;

  const getDisabledReasons = () => {
    const reasons = [];
    if (user?.tier === 'FREE') reasons.push("UPGRADE REQUIRED: Subscribing to any plan allows you to start posting and growing your game's presence.");
    if (!hasContent) reasons.push("Content is required for at least one language");
    if (selectedPlatforms.length === 0) reasons.push("Select at least one social platform");
    if (hasValidationErrors) reasons.push("Fix video/file validation errors (duration or size)");
    if (isFetchingTiktokInfo) reasons.push("Checking TikTok account status...");
    if (mediaBlockingIssues.hasIssues) reasons.push(mediaBlockingIssues.message);

    if (hasTikTokSelected) {
      if (!tiktokTitle.trim() && !Object.values(translations).some(t => t.tiktokTitle?.trim())) reasons.push("TikTok: Video Title is required");
      if (!tiktokPrivacy) reasons.push("TikTok: Privacy Status must be selected");
      if (tiktokContentDisclosure && !tiktokYourBrand && !tiktokBrandedContent) {
        reasons.push("TikTok: You need to indicate if your content promotes yourself, a third party, or both.");
      }
      const durationToCheck = ((videoEndTime ?? videoDuration) || 0) - (videoStartTime ?? 0);
      const totalDuration = globalState.addEndLogo ? durationToCheck + 9 : durationToCheck;
      if (totalDuration && totalDuration > tiktokRestrictions.maxDuration) {
        reasons.push(`TikTok: Video duration exceeds limit of ${tiktokRestrictions.maxDuration}s${globalState.addEndLogo ? ' (including 9s logo)' : ''}`);
      }

      const apiError = selectedTikTokAccounts
        .map(a => tiktokCreatorInfo[a.socialAccount.id]?.error)
        .find(e => e && e.code !== 'ok');
      if (apiError) {
        reasons.push(`TikTok API Error: ${apiError.message || apiError.code}. Try again later.`);
      }
    }

    // Check if translations exist for all selected platform languages
    selectedPlatforms.forEach(id => {
      const acc = activeProject?.socialAccounts?.find(sa => sa.socialAccount.id === id);
      if (acc && acc.socialAccount.language) {
        const lang = acc.socialAccount.language;
        if (!translations[lang] || !translations[lang].text?.trim()) {
          reasons.push(`Missing translation for ${acc.socialAccount.name} (${lang.toUpperCase()})`);
        }
      }
    });

    if (hasTumblrSelected) {
      if (tumblrContentDisclosure && !tumblrYourBrand && !tumblrBrandedContent) {
        reasons.push("Tumblr: You need to indicate if your content promotes yourself, a third party, or both.");
      }
    }

    return reasons;
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-[100vw] w-[100vw] h-[100vh] max-h-[100vh] bg-background border-border p-0 flex flex-col overflow-hidden text-foreground rounded-none">
        <DialogHeader className="p-6 border-b border-border flex flex-col md:flex-row items-center justify-between shrink-0 bg-muted/20">
          <DialogTitle className="text-2xl font-bold flex items-center justify-between w-full pr-12">
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {isReadMode ? (
                  <>
                    <CheckCircle2 className="h-6 w-6 text-green-500 shrink-0" />
                    <span className="text-foreground truncate">
                      Post Group: {Object.values(translations)[0]?.text || ''}
                    </span>
                  </>
                ) : (
                  <>
                    <Sparkles className="h-6 w-6 text-primary shrink-0" />
                    <span className="text-foreground">Create New Post</span>
                  </>
                )}
              </div>
            </div>

            <div className="flex items-center gap-4 shrink-0 ml-4">
              {isReadMode && currentPost?.schedules && currentPost.schedules.length > 0 && (
                <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                  <Clock className="h-3 w-3 text-primary/70" />
                  <span className="whitespace-nowrap uppercase font-black tracking-widest text-[9px]">
                    Sent {moment(currentPost.schedules[0].scheduledAt).format('MMM D, YYYY [at] HH:mm')}
                  </span>
                </div>
              )}

              {isReadMode && currentPost?.schedules && currentPost.schedules.length > 0 && currentPost?.gameName && (
                <div className="hidden md:block h-4 w-[1px] bg-card" />
              )}

              {currentPost?.gameName && (
                <Badge variant="outline" className="bg-primary/10 border-primary/30 text-primary font-black px-3 py-1 rounded-lg flex items-center gap-2">
                  <Gamepad2 className="h-3.5 w-3.5" />
                  {currentPost.gameName}
                </Badge>
              )}

              {isReadMode && currentPost?.id && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 text-rose-500 hover:text-rose-400 hover:bg-rose-500/10 gap-2 font-black uppercase text-[10px] tracking-widest"
                  onClick={handleArchivePost}
                  disabled={isPublishing}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete Post
                </Button>
              )}
            </div>
          </DialogTitle>
        </DialogHeader>

        <div
          className="flex-1 overflow-hidden"
          onDragEnter={(e) => {
            if (e.dataTransfer.types.includes('Files')) {
              e.preventDefault();
            }
          }}
          onDragOver={(e) => {
            // Allow drop on the entire modal area (fixes Radix ScrollArea intercepting drag events)
            if (e.dataTransfer.types.includes('Files')) {
              e.preventDefault();
              e.dataTransfer.dropEffect = 'copy';
            }
          }}
          onDrop={(e) => {
            if (!isReadMode && e.dataTransfer.files.length > 0) {
              e.preventDefault();
              e.stopPropagation();
              const files = Array.from(e.dataTransfer.files).filter(f => {
                const type = f.type.toLowerCase();
                const ext = f.name.toLowerCase().split('.').pop() || '';
                const validExts = ['mp4', 'mov', 'avi', 'webm', 'mkv', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'];
                return type.startsWith('image/') || type.startsWith('video/') || validExts.includes(ext);
              });
              if (files.length > 0) {
                handleAddMoreMedia(files);
              }
            }
          }}
        >
          <ScrollArea className="h-full px-6 py-6">
            <div className={cn(
              "grid gap-8 mb-20",
              isReadMode ? "grid-cols-1 lg:grid-cols-12" : "grid-cols-1 lg:grid-cols-2"
            )}>
              {/* Left Column: Media (Editor) / Media & Details (Read) */}
              <div className={cn("space-y-6", isReadMode ? "lg:col-span-7" : "")}>
                {!isReadMode ? (
                  <div className="flex gap-6 items-start">
                    <div className={cn(
                      "shrink-0 transition-all duration-500 rounded-[22px]",
                      shouldHighlightSocials && "ring-2 ring-inset ring-primary bg-primary/10 animate-pulse"
                    )}>
                      <SocialMediaSelector
                        disabledAccountIds={multiMediaDisabled.ids}
                        disabledReason={multiMediaDisabled.reason}
                      />
                    </div>
                    <div className="flex-1 min-h-[400px] flex flex-col">
                      {/* Top bar with tabs or Add Media button */}
                      <div className="mb-4 flex items-center justify-between gap-3">
                        {!isReadMode && (hasVerticalPlatforms || hasHorizontalPlatforms) && !unifyFormat ? (
                          <Tabs value={activeTab} onValueChange={(v: any) => setActiveTab(v)}>
                            <TabsList className="bg-muted/50 border border-border p-1 rounded-xl h-10 w-fit">
                              {hasVerticalPlatforms && (
                                <TabsTrigger value="mobile" className="text-[10px] font-black uppercase tracking-widest rounded-lg px-6">
                                  Mobile
                                </TabsTrigger>
                              )}
                              {hasHorizontalPlatforms && (
                                <TabsTrigger value="desktop" className="text-[10px] font-black uppercase tracking-widest rounded-lg px-6">
                                  Desktop
                                </TabsTrigger>
                              )}
                            </TabsList>
                          </Tabs>
                        ) : (
                          <div></div>
                        )}
                        
                        {/* Add More Media Button */}
                        {!isReadMode && mediaItems.length > 0 && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const input = document.createElement('input');
                              input.type = 'file';
                              input.accept = 'image/*,video/*';
                              input.multiple = true;
                              input.onchange = (e: any) => {
                                const files = Array.from(e.target.files || []) as File[];
                                if (files.length > 0) {
                                  handleAddMoreMedia(files);
                                }
                              };
                              input.click();
                            }}
                            disabled={uploading}
                            className="border-border hover:border-primary hover:bg-primary/10 text-foreground hover:text-primary h-10"
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Add Media
                          </Button>
                        )}
                      </div>

                      <div className="flex-1">
                        <MultiMediaPreview
                          mediaItems={mediaItems}
                          onMediaItemsChange={setMediaItems}
                          selectedPlatforms={selectedPlatforms}
                          selectedPlatformNames={selectedPlatforms
                            .map(accountId => {
                              const account = activeProject?.socialAccounts?.find(sa => sa.socialAccount.id === accountId);
                              return account?.socialAccount.platform;
                            })
                            .filter((platform): platform is string => platform !== undefined)}
                          onError={setError}
                          onWarning={setWarning}
                          needsMobileFormat={!unifyFormat && activeTab === 'mobile'}
                          onOpenCropper={() => setShowCropper(true)}
                          uploading={uploading}
                          generatingCaptions={generatingCaptions}
                          onRecommendVideo={hasRecommendationAvailable ? handleRecommendVideo : undefined}
                          recommendingVideo={recommendingVideo}
                          onCategorySelect={setUserSelectedCategory}
                          selectedCategory={userSelectedCategory}
                          unifyFormat={unifyFormat}
                          onUnifyFormatChange={(unify) => {
                            setUnifyFormat(unify);
                            if (!unify) {
                              setActiveTab('mobile');
                            } else {
                              setActiveTab('desktop');
                            }
                          }}
                          showUnifyOption={hasHorizontalPlatforms && hasVerticalPlatforms}
                          maxDuration={globalMaxDuration}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex gap-4">
                      {(() => {
                        const firstEmbeddable = currentPost?.schedules?.find(s =>
                          (s.socialAccount?.platform?.toLowerCase().includes('youtube') ||
                            s.socialAccount?.platform?.toLowerCase().includes('tiktok') ||
                            s.socialAccount?.platform?.toLowerCase().includes('bluesky')) &&
                          s.externalPostId &&
                          s.status === 'COMPLETED'
                        );

                        const hasLocalMedia = mediaPath && !mediaPath.includes('deleted');

                        if (!hasLocalMedia && firstEmbeddable) {
                          const platform = firstEmbeddable.socialAccount?.platform || '';
                          return (
                            <div className="flex-1 space-y-2">
                              <Label className="text-muted-foreground font-medium text-[10px] uppercase font-black tracking-widest px-1">Embedded Media</Label>
                              <UniversalEmbedPlayer
                                platform={platform}
                                externalId={firstEmbeddable.externalPostId!}
                                className="w-full"
                              />
                            </div>
                          );
                        }

                        return (
                          <>
                            {/* When we have multiple media items, show gallery + main preview */}
                            {hasLocalMedia && mediaItems.length > 1 && (
                              <div className="flex-1 space-y-3">
                                <Label className="text-muted-foreground font-medium text-[10px] uppercase font-black tracking-widest px-1">Media ({mediaItems.length} items)</Label>
                                <div className="flex gap-3 flex-wrap">
                                  {mediaItems.map((item, idx) => (
                                    <div
                                      key={item.id || idx}
                                      className="relative w-24 h-24 rounded-xl overflow-hidden border-2 border-border bg-card flex-shrink-0"
                                    >
                                      {item.type === 'video' ? (
                                        <video
                                          src={item.path ? `${API_BASE_URL}/social/media?path=${encodeURIComponent(item.path)}&token=${localStorage.getItem('token')}` : undefined}
                                          className="w-full h-full object-cover"
                                          muted
                                          playsInline
                                        />
                                      ) : (
                                        <img
                                          src={item.preview || (item.path ? `${API_BASE_URL}/social/media?path=${encodeURIComponent(item.path)}&token=${localStorage.getItem('token')}` : '')}
                                          alt={`Media ${idx + 1}`}
                                          className="w-full h-full object-cover"
                                        />
                                      )}
                                      <div className="absolute bottom-0 right-0 w-5 h-5 rounded-full bg-black/80 flex items-center justify-center text-[10px] font-bold text-white">
                                        {idx + 1}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                <div className="bg-black overflow-hidden border border-border">
                                  {mediaPath.toLowerCase().match(/\.(mp4|mov|avi|mkv|webm|m4v)/) ? (
                                    <video
                                      src={`${API_BASE_URL}/social/media?path=${encodeURIComponent(mediaPath)}&token=${localStorage.getItem('token')}`}
                                      poster={currentPost?.thumbnailPreview}
                                      controls
                                      className="w-full"
                                      onError={() => setMediaLoadError(true)}
                                    />
                                  ) : (
                                    <img
                                      src={`${API_BASE_URL}/social/media?path=${encodeURIComponent(mediaPath)}&token=${localStorage.getItem('token')}`}
                                      alt=""
                                      className="w-full object-contain"
                                      onError={() => setMediaLoadError(true)}
                                    />
                                  )}
                                </div>
                              </div>
                            )}
                            {/* Single media or crop formats: show desktop/mobile when crop data exists */}
                            {(currentPost?.config?.desktopConfig?.cropData || (!currentPost?.config?.mobileConfig && currentPost?.config?.cropData)) && (
                              <div className="flex-1 space-y-2">
                                <Label className="text-muted-foreground font-medium text-[10px] uppercase font-black tracking-widest px-1">Desktop Format</Label>
                                <div className={cn(
                                  "bg-black overflow-hidden border border-border shadow-2xl flex items-center justify-center transition-all duration-500 mx-auto aspect-video"
                                )}>
                                  {mediaPath.toLowerCase().match(/\.(mp4|mov|avi|mkv|webm|m4v)/) ? (
                                    <video
                                      src={`${API_BASE_URL}/social/media?path=${encodeURIComponent(mediaPath)}&token=${localStorage.getItem('token')}`}
                                      poster={currentPost?.thumbnailPreview}
                                      controls
                                      className="max-w-full max-h-full"
                                      onError={() => setMediaLoadError(true)}
                                    />
                                  ) : (
                                    <img
                                      src={`${API_BASE_URL}/social/media?path=${encodeURIComponent(mediaPath)}&token=${localStorage.getItem('token')}`}
                                      alt=""
                                      className="max-w-full max-h-full"
                                      onError={() => setMediaLoadError(true)}
                                    />
                                  )}
                                </div>
                              </div>
                            )}
                            {currentPost?.config?.mobileConfig?.cropData && (
                              <div className="flex-1 space-y-2">
                                <Label className="text-muted-foreground font-medium text-[10px] uppercase font-black tracking-widest px-1">Mobile Format</Label>
                                <div className={cn(
                                  "bg-black overflow-hidden border border-border shadow-2xl flex items-center justify-center transition-all duration-500 mx-auto aspect-[9/16] max-h-[400px]"
                                )}>
                                  {mediaPath.toLowerCase().match(/\.(mp4|mov|avi|mkv|webm|m4v)/) ? (
                                    <video
                                      src={`${API_BASE_URL}/social/media?path=${encodeURIComponent(mediaPath)}&token=${localStorage.getItem('token')}`}
                                      poster={currentPost?.thumbnailPreview}
                                      controls
                                      className="max-w-full max-h-full"
                                      onError={() => setMediaLoadError(true)}
                                    />
                                  ) : (
                                    <img
                                      src={`${API_BASE_URL}/social/media?path=${encodeURIComponent(mediaPath)}&token=${localStorage.getItem('token')}`}
                                      alt=""
                                      className="max-w-full max-h-full"
                                      onError={() => setMediaLoadError(true)}
                                    />
                                  )}
                                </div>
                              </div>
                            )}
                            {/* Fallback: single media when no crop data (so we always show something) */}
                            {hasLocalMedia && mediaItems.length <= 1 && !currentPost?.config?.desktopConfig?.cropData && !currentPost?.config?.mobileConfig?.cropData && (
                              <div className="flex-1 space-y-2">
                                <Label className="text-muted-foreground font-medium text-[10px] uppercase font-black tracking-widest px-1">Media</Label>
                                <div className="bg-black overflow-hidden border border-border shadow-2xl mx-auto">
                                  {mediaPath.toLowerCase().match(/\.(mp4|mov|avi|mkv|webm|m4v)/) ? (
                                    <video
                                      src={`${API_BASE_URL}/social/media?path=${encodeURIComponent(mediaPath)}&token=${localStorage.getItem('token')}`}
                                      poster={currentPost?.thumbnailPreview}
                                      controls
                                      className="w-full"
                                      onError={() => setMediaLoadError(true)}
                                    />
                                  ) : (
                                    <img
                                      src={`${API_BASE_URL}/social/media?path=${encodeURIComponent(mediaPath)}&token=${localStorage.getItem('token')}`}
                                      alt=""
                                      className="w-full object-contain"
                                      onError={() => setMediaLoadError(true)}
                                    />
                                  )}
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>

                    {/* Post Content & Classification (Moved under video) */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-muted/40 p-5 rounded-2xl border border-border/50 space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-[10px] font-black text-muted-foreground uppercase tracking-[0.2em]">
                            <MessageSquare className="h-3 w-3" />
                            Post Content
                          </div>

                          {/* View on X Button */}
                          {(() => {
                            const twitterSchedule = currentPost?.schedules?.find(s =>
                              s.externalPostId &&
                              (
                                (s.socialAccount?.platform?.toLowerCase().includes('twitter') || s.socialAccount?.platform?.toLowerCase().includes('x')) ||
                                (s.socialAccountId.toLowerCase().includes('twitter') || s.socialAccountId.toLowerCase().includes('x'))
                              )
                            );
                            if (twitterSchedule?.externalPostId) {
                              return (
                                <a
                                  href={`https://twitter.com/i/web/status/${twitterSchedule.externalPostId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-1.5 px-2 py-1 bg-white/5 hover:bg-white/10 rounded-md border border-white/10 transition-colors group"
                                >
                                  <svg className="h-3 w-3 fill-white group-hover:scale-110 transition-transform" viewBox="0 0 24 24">
                                    <path d="M18.244 2.25h3.308l-7.227 7.748L22.875 22.5h-6.652l-5.211-6.817L4.99 22.5H1.68l7.73-8.235L1.25 2.25h6.822l4.713 6.176 5.459-6.176zM16.083 20.5h1.832L7.676 4.5H5.71l10.373 16z" />
                                  </svg>
                                  <span className="text-[9px] font-bold text-white uppercase tracking-widest">View on X</span>
                                </a>
                              );
                            }
                            return null;
                          })()}
                        </div>
                        <div className="space-y-4">
                          {Object.keys(translations).map(lang => (
                            <div key={lang} className="space-y-1.5">
                              <span className="text-[9px] font-black text-primary uppercase tracking-widest">{lang.toUpperCase()}</span>
                              <div className="text-sm text-foreground bg-background/40 p-4 rounded-xl border border-border/50 leading-relaxed italic">
                                "{translations[lang].text}"
                              </div>
                              {translations[lang].tiktokTitle && (
                                <p className="text-[10px] text-muted-foreground font-bold ml-1">
                                  TikTok Title: <span className="text-primary/70">{translations[lang].tiktokTitle}</span>
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="bg-muted/40 p-5 rounded-2xl border border-border/50 space-y-4">
                        <div className="flex items-center gap-2 text-[10px] font-black text-muted-foreground uppercase tracking-[0.2em]">
                          <Tags className="h-3 w-3" />
                          Classification
                        </div>

                        <div className="space-y-4">
                          {/* Content Type */}
                          {currentPost?.media?.some((m: any) => m.mediaType === 'video') && (() => {
                            const videoMedia = currentPost?.media?.find((m: any) => m.mediaType === 'video') as any;
                            const ct = videoMedia?.contentType;
                            const ctStyles: Record<string, string> = {
                              TRAILER: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
                              GAMEPLAY: 'bg-green-500/20 text-green-400 border-green-500/30',
                              FEATURE: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
                              SURVEY: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
                            };
                            const updateCT = async (newCT: string | null) => {
                              if (!videoMedia?.id) return;
                              try {
                                await axios.patch(`${API_BASE_URL}/social/media/${videoMedia.id}/metadata`, { contentType: newCT }, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                if (currentPost?.id) {
                                  queryClient.invalidateQueries({ queryKey: ['post', currentPost.id] });
                                }
                                toast.success('Content type updated');
                              } catch {
                                toast.error('Failed to update content type');
                              }
                            };
                            return (
                              <div className="space-y-2">
                                <span className="text-[9px] font-black text-muted-foreground uppercase tracking-widest">Content Type</span>
                                <div className="flex flex-wrap gap-1.5">
                                  {['TRAILER', 'GAMEPLAY', 'FEATURE', 'SURVEY'].map(type => (
                                    <Badge
                                      key={type}
                                      className={cn(
                                        "px-2.5 py-1 text-[9px] font-black uppercase tracking-wider cursor-pointer transition-all border",
                                        ct === type
                                          ? ctStyles[type]
                                          : "bg-muted/50 text-muted-foreground border-border hover:border-primary/50"
                                      )}
                                      onClick={() => updateCT(ct === type ? null : type)}
                                    >
                                      {type}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            );
                          })()}

                          <div className="space-y-2">
                            <span className="text-[9px] font-black text-muted-foreground uppercase tracking-widest">Post Tags</span>
                            <div className="flex flex-wrap gap-1.5">
                              {selectedTags.length > 0 ? selectedTags.map((tag, i) => (
                                <Badge key={i} className="bg-primary/10 text-primary border-primary/20 px-2.5 py-1 text-xs font-bold">
                                  {tag}
                                </Badge>
                              )) : <span className="text-[10px] text-muted-foreground italic">No post tags</span>}
                            </div>
                          </div>

                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <span className="text-[9px] font-black text-muted-foreground uppercase tracking-widest">Evergreen Post Tags</span>
                              {!isReadMode && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-4 w-4 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-full"
                                  onClick={() => setIsEvergreenSettingsOpen(true)}
                                >
                                  <Settings className="h-2.5 w-2.5" />
                                </Button>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {Array.isArray(evergreenTags) && evergreenTags.length > 0 ? evergreenTags.map((tag, i) => (
                                <Badge key={i} className="bg-green-600/10 text-green-400 border-green-500/20 px-2.5 py-1 text-xs font-bold">
                                  {tag}
                                </Badge>
                              )) : <span className="text-[10px] text-muted-foreground italic">No evergreen tags</span>}
                            </div>
                          </div>

                          {isReadMode && (currentPost?.tiktokMetadata || currentPost?.config?.tiktokMetadata) && (
                            <div className="space-y-2 pt-2 border-t border-border/50">
                              <span className="text-[9px] font-black text-primary uppercase tracking-widest flex items-center gap-1.5">
                                <Music2 className="h-3 w-3" />
                                TikTok Settings
                              </span>
                              <div className="flex flex-wrap gap-1.5">
                                <Badge variant="outline" className="bg-primary/5 text-primary/70 border-primary/20 text-[9px] font-bold uppercase">
                                  Title: {(currentPost?.tiktokMetadata?.title || currentPost?.config?.tiktokMetadata?.title)?.substring(0, 20)}...
                                </Badge>
                                <Badge variant="outline" className="bg-primary/5 text-primary/70 border-primary/20 text-[9px] font-bold uppercase">
                                  Privacy: {currentPost?.tiktokMetadata?.privacyLevel || currentPost?.config?.tiktokMetadata?.privacyLevel}
                                </Badge>
                              </div>
                            </div>
                          )}

                          {isReadMode && (currentPost?.youtubeMetadata || currentPost?.config?.youtubeMetadata) && (
                            <div className="space-y-2 pt-2 border-t border-border/50">
                              <span className="text-[9px] font-black text-red-400 uppercase tracking-widest flex items-center gap-1.5">
                                <Youtube className="h-3 w-3" />
                                YouTube Settings
                              </span>
                              <div className="flex flex-wrap gap-1.5">
                                <Badge variant="outline" className="bg-red-500/5 text-red-400/70 border-red-500/20 text-[9px] font-bold uppercase tracking-widest">
                                  Format: {(currentPost?.youtubeMetadata?.videoType || currentPost?.config?.youtubeMetadata?.videoType) === 'short' ? 'YouTube Short' : 'Standard Video'}
                                </Badge>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column (Editor Content) OR (Analytics & Comments) */}
              {!isReadMode ? (
                <div className="space-y-6">
                  {trendId && trendData && (
                    <>
                      <div className="bg-muted/50 p-5 rounded-2xl border-2 border-primary/30 space-y-4 shadow-xl shadow-primary/5 animate-in fade-in slide-in-from-top-4 duration-500">
                        <div className="flex items-center justify-between">
                          <div className="flex flex-col">
                            <Label className="text-[11px] font-black text-primary uppercase tracking-[0.2em] flex items-center gap-2 mb-1">
                              <TrendingUp className="h-3.5 w-3.5" />
                              Selected Quote Target
                            </Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setIsTargetDialogOpen(true)}
                              className="h-7 px-3 bg-primary/10 border-primary/30 text-primary hover:bg-primary/20 text-[10px] font-black uppercase tracking-widest gap-2"
                            >
                              <RefreshCw className="h-3 w-3" />
                              Change Quote target
                            </Button>
                            <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20 px-2 py-0.5 font-bold uppercase tracking-tighter text-[9px]">
                              {trendData.name}
                            </Badge>
                          </div>
                        </div>

                        {(() => {
                          const selectedEvidence = uniqueEvidences.find(e => e.url.split('/').pop() === quoteTweetId);
                          if (!selectedEvidence) return <div className="p-4 bg-background/40 border border-border rounded-xl text-center text-xs text-muted-foreground italic">No target selected. Click "Change" to choose one.</div>;

                          return (
                            <div className="relative group">
                              <div className="bg-background/60 border border-primary/30 rounded-xl p-4 transition-all hover:bg-background/80">
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-3">
                                    <Avatar className="h-10 w-10 border border-border">
                                      <AvatarImage src={selectedEvidence.authorAvatar} />
                                      <AvatarFallback className="bg-card text-xs font-black">{selectedEvidence.authorName?.charAt(0)}</AvatarFallback>
                                    </Avatar>
                                    <div className="min-w-0">
                                      <div className="text-sm font-black text-foreground truncate">{selectedEvidence.authorName}</div>
                                      <div className="text-xs text-muted-foreground font-mono">{selectedEvidence.authorHandle ? (selectedEvidence.authorHandle.startsWith('@') ? selectedEvidence.authorHandle : `@${selectedEvidence.authorHandle}`) : ''}</div>
                                    </div>
                                  </div>
                                  <div className="flex flex-col items-end gap-1">
                                    <div className="flex items-center gap-3">
                                      <div className="flex items-center gap-1.5 text-xs font-bold text-foreground">
                                        <Users className="h-3 w-3 text-primary" />
                                        {(selectedEvidence.authorFollowers || 0).toLocaleString()}
                                      </div>
                                      <div className="flex items-center gap-1.5 text-xs font-bold text-foreground">
                                        <Heart className="h-3 w-3 text-rose-500" />
                                        {(selectedEvidence.likeCount || 0).toLocaleString()}
                                      </div>
                                    </div>
                                    <Badge className="bg-green-500/10 text-green-400 border-green-500/20 text-[8px] font-black uppercase tracking-tighter">Selected Target</Badge>
                                  </div>
                                </div>
                                <p className="text-xs text-foreground leading-relaxed line-clamp-2 italic mb-3">"{selectedEvidence.text}"</p>
                                <div className="flex items-center justify-between border-t border-border/50 pt-3">
                                  <span className="text-[9px] text-muted-foreground font-bold uppercase tracking-widest">{moment(selectedEvidence.publicationDate).format('MMM D, YYYY')}</span>
                                  <div className="flex items-center gap-2">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => window.open(selectedEvidence.url, '_blank')}
                                      className="h-6 text-[10px] text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 gap-1.5 px-2"
                                    >
                                      <ExternalLink className="h-3 w-3" /> View Post
                                    </Button>
                                    <HoverCard openDelay={150}>
                                      <HoverCardTrigger asChild>
                                        <Button variant="ghost" size="sm" className="h-6 text-[10px] text-primary hover:text-primary/80 hover:bg-primary/10 gap-1.5 px-2">
                                          <Eye className="h-3 w-3" /> Quick Preview
                                        </Button>
                                      </HoverCardTrigger>
                                      <HoverCardContent side="top" align="center" className="p-0 bg-background border-border shadow-2xl overflow-hidden w-[320px]">
                                        <SimulatedTweet
                                          text={selectedEvidence.text}
                                          url={selectedEvidence.url}
                                          date={selectedEvidence.publicationDate}
                                          authorName={selectedEvidence.authorName}
                                          authorHandle={selectedEvidence.authorHandle}
                                          authorAvatar={selectedEvidence.authorAvatar}
                                          mediaUrl={selectedEvidence.mediaUrl}
                                          likeCount={selectedEvidence.likeCount}
                                          retweetCount={selectedEvidence.retweetCount}
                                        />
                                      </HoverCardContent>
                                    </HoverCard>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                      </div>

                      {/* Target Selection Dialog */}
                      <Dialog open={isTargetDialogOpen} onOpenChange={setIsTargetDialogOpen}>
                        <DialogContent className="max-w-2xl bg-background border-border p-0 overflow-hidden flex flex-col max-h-[80vh]">
                          <div className="p-6 border-b border-border shrink-0">
                            <DialogTitle className="text-xl font-bold flex items-center gap-2 text-foreground">
                              <TrendingUp className="h-5 w-5 text-primary" />
                              Select Quote Tweet Target
                            </DialogTitle>
                            <p className="text-xs text-muted-foreground mt-1 uppercase font-black tracking-widest">Available targets for {trendData.name}</p>
                          </div>
                          <ScrollArea className="flex-1">
                            <div className="p-6 grid grid-cols-1 gap-3">
                              {uniqueEvidences.map((evidence: any, idx: number) => {
                                const tId = evidence.url.split('/').pop();
                                const isSelected = quoteTweetId === tId;
                                return (
                                  <button
                                    key={idx}
                                    onClick={() => {
                                      setQuoteTweetId(tId);
                                      setIsTargetDialogOpen(false);
                                    }}
                                    className={cn(
                                      "flex items-center justify-between p-4 rounded-xl border-2 transition-all text-left group relative",
                                      isSelected
                                        ? "bg-primary/10 border-primary text-foreground shadow-lg shadow-primary/5"
                                        : "bg-muted/40 border-border/50 text-muted-foreground hover:border-border hover:bg-muted"
                                    )}
                                  >
                                    <div className="flex items-center gap-4 min-w-0">
                                      <Avatar className="h-11 w-11 border border-border group-hover:border-border">
                                        <AvatarImage src={evidence.authorAvatar} />
                                        <AvatarFallback className="bg-card text-sm font-black">{evidence.authorName?.charAt(0)}</AvatarFallback>
                                      </Avatar>
                                      <div className="min-w-0">
                                        <div className="text-sm font-black text-foreground truncate group-hover:text-primary transition-colors">
                                          {evidence.authorName}
                                        </div>
                                        <div className="text-xs text-muted-foreground font-mono flex items-center gap-2">
                                          {evidence.authorHandle ? (evidence.authorHandle.startsWith('@') ? evidence.authorHandle : `@${evidence.authorHandle}`) : ''}
                                          <span className="opacity-30">•</span>
                                          <span className="text-[10px] opacity-70">{moment(evidence.publicationDate).format('MMM D, YYYY')}</span>
                                        </div>
                                        <p className="text-[11px] text-muted-foreground line-clamp-1 mt-1 font-medium italic">"{evidence.text}"</p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-6 shrink-0 ml-4">
                                      <div className="flex flex-col items-end">
                                        <div className="flex items-center gap-3">
                                          <div className="flex flex-col items-end">
                                            <div className="flex items-center gap-1.5 text-xs font-bold text-foreground">
                                              <Users className="h-3 w-3 text-primary" />
                                              {(evidence.authorFollowers || 0).toLocaleString()}
                                            </div>
                                            <div className="text-[9px] text-muted-foreground uppercase font-black tracking-tighter">
                                              Followers
                                            </div>
                                          </div>
                                          <div className="flex flex-col items-end">
                                            <div className="flex items-center gap-1.5 text-xs font-bold text-foreground">
                                              <Heart className="h-3 w-3 text-rose-500" />
                                              {(evidence.likeCount || 0).toLocaleString()}
                                            </div>
                                            <div className="text-[9px] text-muted-foreground uppercase font-black tracking-tighter">
                                              Likes
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          window.open(evidence.url, '_blank');
                                        }}
                                        className="h-7 px-2 text-[10px] text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 gap-1.5"
                                      >
                                        <ExternalLink className="h-3 w-3" /> View
                                      </Button>
                                      {isSelected ? (
                                        <CheckCircle2 className="h-5 w-5 text-primary" />
                                      ) : (
                                        <div className="w-5 h-5 rounded-full border-2 border-border group-hover:border-border" />
                                      )}
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          </ScrollArea>
                          <div className="p-4 bg-muted/50 border-t border-border flex justify-end shrink-0">
                            <Button variant="outline" onClick={() => setIsTargetDialogOpen(false)} className="border-border text-foreground hover:bg-card">
                              Close
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </>
                  )}

                  <div className="space-y-6">
                    <div className="space-y-4">
                      {user?.featureFlags?.auto_translate && languagesNeeded.length > 1 && (
                        <div className="flex justify-end items-center gap-2 px-1">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={uploading || isTranslating}
                                className="h-8 px-3 text-muted-foreground hover:text-foreground text-[10px] font-black uppercase tracking-widest gap-2"
                              >
                                <RefreshCw className="h-3.5 w-3.5" />
                                Swap Main Language
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-card border-border text-foreground shadow-xl">
                              {languagesNeeded.map((lang) => (
                                <DropdownMenuItem
                                  key={lang}
                                  onClick={() => {
                                    setBaseLanguage(lang);
                                    userSwappedRef.current = true;
                                  }}
                                  className={cn(
                                    "flex items-center gap-2 cursor-pointer hover:bg-primary/10",
                                    lang === baseLanguage && "bg-primary/20 text-primary font-bold"
                                  )}
                                >
                                  <span className="text-sm">
                                    {languageNames[lang] || lang.toUpperCase()}
                                  </span>
                                  {lang === baseLanguage && <CheckCircle2 className="h-3.5 w-3.5 ml-auto" />}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>

                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleTranslate(baseLanguage)}
                            disabled={isTranslating || !translations[baseLanguage]?.text?.trim() || uploading}
                            className="h-8 px-4 bg-emerald-600/5 border-emerald-500/20 text-emerald-400 hover:bg-emerald-600/10 text-[10px] font-black uppercase tracking-widest gap-2 shadow-lg shadow-emerald-500/5 rounded-xl border-2"
                          >
                            {isTranslating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Globe className="h-3.5 w-3.5" />}
                            Translate All from {languageNames[baseLanguage] || baseLanguage.toUpperCase()}
                          </Button>
                        </div>
                      )}
                      {[...languagesNeeded].sort((a, b) => (a === baseLanguage ? -1 : b === baseLanguage ? 1 : 0)).map((lang) => (
                        <AccountTextInput
                          key={lang}
                          label={`Post text (${lang.toUpperCase()})`}
                          text={translations[lang]?.text || ''}
                          tiktokTitle={translations[lang]?.tiktokTitle || ''}
                          checked={true}
                          disabled={uploading || isReadMode}
                          uploading={uploading}
                          onTextChange={(text) => setTranslations(prev => ({
                            ...prev,
                            [lang]: { ...prev[lang], text }
                          }))}
                          onTiktokTitleChange={(title) => setTranslations(prev => ({
                            ...prev,
                            [lang]: { ...prev[lang], tiktokTitle: title }
                          }))}
                          onCheckedChange={() => { }}
                          onRemove={lang !== 'en' && lang !== baseLanguage ? () => {
                            const newTrans = { ...translations };
                            delete newTrans[lang];
                            setTranslations(newTrans);
                          } : undefined}
                          placeholder={`What's on your mind? (${lang.toUpperCase()})`}
                          hideCheckbox={true}
                          maxCharacters={minCharacterLimit}
                          language={lang}
                          onGenerateCaptions={!isReadMode && (mediaPath || localFile) ? handleGenerateCaptions : undefined}
                          isGeneratingCaptions={generatingCaptions}
                          showTiktokTitle={hasTikTokSelected}
                          isTranslating={isTranslating}
                          isBaseLanguage={lang === baseLanguage}
                        />
                      ))}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-muted/30 p-6 rounded-2xl border border-border/50">
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-foreground font-medium">
                          <Tags className="h-4 w-4 text-primary" />
                          <Label>Post Tags</Label>
                        </div>
                        <div className="flex flex-wrap gap-2 min-h-8">
                          {selectedTags.map((tag, index) => {
                            const isInactive = inactiveTags.includes(tag);
                            return (
                              <Badge
                                key={index}
                                className={cn(
                                  "transition-all duration-200 cursor-pointer select-none",
                                  isInactive
                                    ? "bg-card text-muted-foreground border-border opacity-60 grayscale"
                                    : "bg-primary/20 text-primary border-primary/30 hover:bg-primary/30"
                                )}
                                onClick={() => toggleTagActive(tag)}
                              >
                                {tag}
                                <X
                                  className="ml-2 h-3 w-3 cursor-pointer hover:text-red-400"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveTag(tag, false);
                                  }}
                                />
                              </Badge>
                            );
                          })}
                        </div>
                        <Input
                          placeholder="Add tag..."
                          disabled={uploading}
                          onKeyDown={(e) => handleTagInputKeyPress(e, false)}
                          onBlur={(e) => {
                            if (e.target.value.trim()) {
                              handleAddTag(e.target.value.trim(), false);
                              e.target.value = '';
                            }
                          }}
                          className="bg-background border-border text-foreground h-10"
                        />
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-foreground font-medium">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-full"
                            onClick={() => setIsEvergreenSettingsOpen(true)}
                          >
                            <Settings className="h-3.5 w-3.5" />
                          </Button>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Label className="cursor-help">Evergreen Post Tags</Label>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="bg-muted border-border text-foreground text-xs p-2 max-w-[200px]">
                                These tags are automatically included. Click to toggle them active/inactive for this post.
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <div className="flex flex-wrap gap-2 min-h-8">
                          {(Array.isArray(evergreenTags) ? evergreenTags : []).map((tag, index) => {
                            const isInactive = inactiveTags.includes(tag);
                            return (
                              <Badge
                                key={index}
                                className={cn(
                                  "transition-all duration-200 cursor-pointer select-none h-7 px-3",
                                  isInactive
                                    ? "bg-card text-muted-foreground border-border opacity-60 grayscale"
                                    : "bg-green-600/20 text-green-400 border-green-500/30 hover:bg-green-600/30 shadow-sm shadow-green-500/5"
                                )}
                                onClick={() => toggleTagActive(tag)}
                              >
                                {tag}
                              </Badge>
                            );
                          })}
                          {evergreenTags.length === 0 && (
                            <p className="text-[10px] text-muted-foreground italic py-1">No evergreen tags configured. Click the gear to add some.</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {hasTikTokSelected && (
                    <div className="bg-muted/40 p-6 rounded-2xl border border-primary/20 space-y-6 animate-in fade-in zoom-in-95 duration-300">
                      <div className="flex items-center gap-2 text-primary">
                        <Music2 className="h-5 w-5" />
                        <h3 className="font-bold text-lg uppercase tracking-tight">TikTok Direct Post Settings</h3>
                      </div>

                      {/* Creator Info */}
                      <div className="flex flex-wrap gap-3">
                        {selectedTikTokAccounts.map(account => {
                          const infoResponse = tiktokCreatorInfo[account.socialAccount.id];
                          const info = infoResponse?.data;
                          const error = infoResponse?.error?.code !== 'ok' ? infoResponse?.error?.message : null;
                          const nickname = info?.creator_nickname || account.socialAccount.name;
                          const handle = info?.creator_username || account.socialAccount.handle;

                          return (
                            <div key={account.socialAccount.id} className="flex items-center gap-2 bg-card/50 px-3 py-1.5 rounded-full border border-border shadow-sm">
                              <div className="h-5 w-5 rounded-full overflow-hidden border border-border">
                                <SocialAvatar
                                  accountId={account.socialAccount.id}
                                  platform={account.socialAccount.platform}
                                  name={nickname}
                                  avatarUrl={info?.creator_avatar_url || account.socialAccount.avatarUrl}
                                />
                              </div>
                              <div className="flex flex-col">
                                <span className="text-[10px] font-bold text-foreground leading-tight">{nickname}</span>
                                <span className="text-[9px] text-muted-foreground leading-tight">@{handle}</span>
                              </div>
                              {isFetchingTiktokInfo && !infoResponse && <Loader2 className="h-3 w-3 animate-spin text-primary" />}
                              {error && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <AlertCircle className="h-3 w-3 text-red-500 cursor-help" />
                                    </TooltipTrigger>
                                    <TooltipContent className="bg-red-950 border-red-500/30 text-red-200 text-[10px] p-2">
                                      {error}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Warnings from creator_info */}
                      {selectedTikTokAccounts.some(account => {
                        const info = tiktokCreatorInfo[account.socialAccount.id];
                        return info?.error?.code && info.error.code !== 'ok';
                      }) && (
                          <div className="bg-red-500/10 border border-red-500/20 p-3 rounded-xl flex items-center gap-3">
                            <AlertTriangle className="h-4 w-4 text-red-500" />
                            <div className="space-y-1">
                              <p className="text-[10px] font-bold text-red-400">
                                Some TikTok accounts have posting restrictions.
                              </p>
                              {selectedTikTokAccounts.map(account => {
                                const error = tiktokCreatorInfo[account.socialAccount.id]?.error;
                                if (error && error.code !== 'ok') {
                                  return (
                                    <p key={account.socialAccount.id} className="text-[9px] text-red-500/80 italic">
                                      {account.socialAccount.name}: {error.message}
                                    </p>
                                  );
                                }
                                return null;
                              })}
                            </div>
                          </div>
                        )}

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Metadata */}
                        <div className="space-y-5">
                          <div className="space-y-2">
                            <Label className="text-muted-foreground text-[10px] uppercase font-black tracking-widest">Video Title (Required)</Label>
                            <Input
                              placeholder="Enter TikTok video title..."
                              value={tiktokTitle}
                              onChange={(e) => setTiktokTitle(e.target.value)}
                              className="bg-background border-border h-11 focus:ring-primary"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label className="text-muted-foreground text-[10px] uppercase font-black tracking-widest">Privacy Status (Required)</Label>
                            <Select
                              value={tiktokPrivacy}
                              onValueChange={(v) => {
                                setTiktokPrivacy(v);
                                if (v === 'SELF_ONLY') {
                                  setTiktokBrandedContent(false);
                                }
                              }}
                            >
                              <SelectTrigger className="bg-background border-border h-11">
                                <SelectValue placeholder="Select who can view this video" />
                              </SelectTrigger>
                              <SelectContent className="bg-card border-border text-foreground">
                                {tiktokRestrictions.allowedPrivacy.includes('PUBLIC_TO_EVERYONE') && (
                                  <SelectItem value="PUBLIC_TO_EVERYONE">Everyone</SelectItem>
                                )}
                                {tiktokRestrictions.allowedPrivacy.includes('MUTUAL_FOLLOW_FRIENDS') && (
                                  <SelectItem value="MUTUAL_FOLLOW_FRIENDS">Friends (Follow each other)</SelectItem>
                                )}
                                {tiktokRestrictions.allowedPrivacy.includes('SELF_ONLY') && (
                                  <SelectItem
                                    value="SELF_ONLY"
                                    disabled={tiktokBrandedContent}
                                    title={tiktokBrandedContent ? "Branded content visibility cannot be set to private." : undefined}
                                    className={cn(
                                      "data-[disabled]:pointer-events-auto data-[disabled]:cursor-not-allowed",
                                      tiktokBrandedContent && "opacity-50"
                                    )}
                                  >
                                    Only Me
                                  </SelectItem>
                                )}
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-3 pt-2">
                            <Label className="text-muted-foreground text-[10px] uppercase font-black tracking-widest">Interactions</Label>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className={cn(
                                      "flex items-center space-x-2 bg-card/30 p-2 rounded-lg border border-border transition-colors cursor-help",
                                      tiktokRestrictions.commentDisabled ? "opacity-50 grayscale" : "hover:border-border"
                                    )}>
                                      <Checkbox
                                        id="allow-comments"
                                        checked={tiktokAllowComments && !tiktokRestrictions.commentDisabled}
                                        onCheckedChange={(v) => setTiktokAllowComments(!!v)}
                                        disabled={tiktokRestrictions.commentDisabled}
                                      />
                                      <Label htmlFor="allow-comments" className="text-xs text-foreground cursor-pointer flex-1">Comments</Label>
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent className="bg-muted border-border text-foreground text-xs">
                                    {tiktokRestrictions.commentDisabled ? "Comments are disabled for this account in TikTok settings" : "Allow users to comment on your video"}
                                  </TooltipContent>
                                </Tooltip>

                                {!(localFile ? localFile.type.startsWith('image/') : (mediaPath?.match(/\.(jpg|jpeg|png|gif|webp)$/i) !== null)) && (
                                  <>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div className={cn(
                                          "flex items-center space-x-2 bg-card/30 p-2 rounded-lg border border-border transition-colors cursor-help",
                                          tiktokRestrictions.duetDisabled ? "opacity-50 grayscale" : "hover:border-border"
                                        )}>
                                          <Checkbox
                                            id="allow-duet"
                                            checked={tiktokAllowDuet && !tiktokRestrictions.duetDisabled}
                                            onCheckedChange={(v) => setTiktokAllowDuet(!!v)}
                                            disabled={tiktokRestrictions.duetDisabled}
                                          />
                                          <Label htmlFor="allow-duet" className="text-xs text-foreground cursor-pointer flex-1">Duet</Label>
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent className="bg-muted border-border text-foreground text-xs">
                                        {tiktokRestrictions.duetDisabled ? "Duets are disabled for this account in TikTok settings" : "Allow users to post their own video side-by-side with yours"}
                                      </TooltipContent>
                                    </Tooltip>

                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div className={cn(
                                          "flex items-center space-x-2 bg-card/30 p-2 rounded-lg border border-border transition-colors cursor-help",
                                          tiktokRestrictions.stitchDisabled ? "opacity-50 grayscale" : "hover:border-border"
                                        )}>
                                          <Checkbox
                                            id="allow-stitch"
                                            checked={tiktokAllowStitch && !tiktokRestrictions.stitchDisabled}
                                            onCheckedChange={(v) => setTiktokAllowStitch(!!v)}
                                            disabled={tiktokRestrictions.stitchDisabled}
                                          />
                                          <Label htmlFor="allow-stitch" className="text-xs text-foreground cursor-pointer flex-1">Stitch</Label>
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent className="bg-muted border-border text-foreground text-xs">
                                        {tiktokRestrictions.stitchDisabled ? "Stitches are disabled for this account in TikTok settings" : "Allow users to use a part of your video in their own video"}
                                      </TooltipContent>
                                    </Tooltip>
                                  </>
                                )}
                              </TooltipProvider>
                            </div>
                          </div>
                        </div>

                        {/* Commercial Disclosure */}
                        <div className="space-y-4 p-5 bg-muted/50 rounded-2xl border border-border shadow-inner">
                          <div className="flex items-center justify-between">
                            <div className="space-y-0.5">
                              <Label className="text-foreground font-bold">Commercial Content Disclosure</Label>
                              <p className="text-xs text-muted-foreground">Does this content promote a brand or service?</p>
                            </div>
                            <Switch
                              checked={tiktokContentDisclosure}
                              onCheckedChange={(v) => {
                                setTiktokContentDisclosure(v);
                                if (!v) {
                                  setTiktokYourBrand(false);
                                  setTiktokBrandedContent(false);
                                }
                              }}
                            />
                          </div>

                          {tiktokContentDisclosure && (
                            <div className="space-y-3 pt-4 animate-in slide-in-from-top-2 duration-300">
                              <div className="flex items-center space-x-3 py-1">
                                <Checkbox
                                  id="your-brand"
                                  checked={tiktokYourBrand}
                                  onCheckedChange={(v) => setTiktokYourBrand(!!v)}
                                />
                                <div className="space-y-0.5 leading-none">
                                  <Label htmlFor="your-brand" className="text-sm font-bold text-foreground cursor-pointer">Your Brand</Label>
                                  <p className="text-xs text-muted-foreground">You are promoting yourself or your own business</p>
                                </div>
                              </div>

                              <div className="flex items-center space-x-3 py-1">
                                <Checkbox
                                  id="branded-content"
                                  checked={tiktokBrandedContent}
                                  disabled={tiktokPrivacy === 'SELF_ONLY'}
                                  onCheckedChange={(v) => setTiktokBrandedContent(!!v)}
                                />
                                <div className="space-y-0.5 leading-none">
                                  <Label
                                    htmlFor="branded-content"
                                    className={cn(
                                      "text-sm font-bold cursor-pointer",
                                      tiktokPrivacy === 'SELF_ONLY' ? "text-muted-foreground" : "text-foreground"
                                    )}
                                  >
                                    Branded Content
                                  </Label>
                                  <p className="text-xs text-muted-foreground">You are promoting another brand or a third party</p>
                                  {tiktokPrivacy === 'SELF_ONLY' && (
                                    <p className="text-xs text-amber-500 font-bold mt-1">Not available when Privacy is set to 'Only Me'.</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {tiktokContentDisclosure && (
                        <div className="space-y-3">
                          {tiktokPrivacy === 'SELF_ONLY' && (
                            <div className="flex items-center gap-3 bg-amber-500/10 border border-amber-500/20 p-3 rounded-xl text-amber-500">
                              <AlertTriangle className="h-5 w-5 shrink-0" />
                              <p className="text-xs font-bold">
                                Branded content visibility cannot be private. Change Privacy Status to enable this option.
                              </p>
                            </div>
                          )}
                          {!tiktokYourBrand && !tiktokBrandedContent && (
                            <div className="flex items-center gap-3 bg-red-500/10 border border-red-500/20 p-3 rounded-xl text-red-400 animate-pulse">
                              <AlertTriangle className="h-5 w-5" />
                              <p className="text-xs font-bold">You need to indicate if your content promotes yourself, a third party, or both.</p>
                            </div>
                          )}
                          {(tiktokYourBrand || tiktokBrandedContent) && (
                            <div className="flex items-center gap-3 bg-primary/10 border border-primary/20 p-3 rounded-xl text-primary">
                              <Info className="h-5 w-5" />
                              <p className="text-xs font-bold">
                                {tiktokBrandedContent
                                  ? "Your photo/video will be labeled as 'Paid partnership'"
                                  : "Your photo/video will be labeled as 'Promotional content'"}
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="space-y-2">
                        <div className="flex items-start gap-3 bg-primary/10 border border-primary/20 p-3 rounded-xl text-primary">
                          <Info className="h-5 w-5 shrink-0" />
                          <p className="text-xs font-bold">
                            {tiktokDeclaration}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {hasYouTubeSelected && (
                    <div className="bg-muted/40 p-6 rounded-2xl border border-red-500/20 space-y-6 animate-in fade-in zoom-in-95 duration-300">
                      <div className="flex items-center gap-2 text-red-400">
                        <Youtube className="h-5 w-5" />
                        <h3 className="font-bold text-lg uppercase tracking-tight">YouTube Post Settings</h3>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="space-y-5">
                          <div className="space-y-2">
                            <Label className="text-muted-foreground text-[10px] uppercase font-black tracking-widest">Video Format</Label>
                            <Tabs value={youtubeVideoType} onValueChange={(v: any) => setYoutubeVideoType(v)} className="w-full">
                              <TabsList className="grid w-full grid-cols-2 bg-card/50 border border-border h-11 p-1 rounded-xl">
                                <TabsTrigger value="video" className="text-[10px] font-black uppercase tracking-widest rounded-lg">Standard Video</TabsTrigger>
                                <TabsTrigger value="short" className="text-[10px] font-black uppercase tracking-widest rounded-lg">YouTube Short</TabsTrigger>
                              </TabsList>
                            </Tabs>
                            <p className="text-[10px] text-muted-foreground italic px-1">
                              {youtubeVideoType === 'short'
                                ? "Shorts are vertical videos under 60 seconds. We'll automatically add #Shorts to the metadata."
                                : "Standard YouTube video format."}
                            </p>
                          </div>
                        </div>

                        <div className="space-y-4 p-5 bg-muted/50 rounded-2xl border border-border shadow-inner flex flex-col justify-center">
                          <div className="flex items-center gap-3">
                            <div className={cn(
                              "p-3 rounded-xl border transition-all flex-1 text-center",
                              youtubeVideoType === 'short' ? "bg-red-500/10 border-red-500/30" : "bg-card/20 border-border"
                            )}>
                              <span className={cn(
                                "text-xs font-black uppercase tracking-widest",
                                youtubeVideoType === 'short' ? "text-red-400" : "text-muted-foreground"
                              )}>
                                {youtubeVideoType === 'short' ? 'Vertical Format Required' : 'Standard Format'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Tumblr Post Settings - Temporarily disabled
                  {hasTumblrSelected && (
                    <div className="bg-muted/40 p-6 rounded-2xl border border-[#35465c]/30 space-y-6 animate-in fade-in zoom-in-95 duration-300">
                      <div className="flex items-center gap-2 text-[#35465c]">
                        <Globe className="h-5 w-5" />
                        <h3 className="font-bold text-lg uppercase tracking-tight">Tumblr Post Settings</h3>
                      </div>

                      <div className="space-y-4 p-5 bg-muted/50 rounded-2xl border border-border shadow-inner">
                        <div className="flex items-center justify-between">
                          <div className="space-y-0.5">
                            <Label className="text-foreground font-bold">Commercial Content Disclosure</Label>
                            <p className="text-xs text-muted-foreground">Does this content promote a brand or service?</p>
                          </div>
                          <Switch
                            checked={tumblrContentDisclosure}
                            onCheckedChange={(v) => {
                              setTumblrContentDisclosure(v);
                              if (!v) {
                                setTumblrYourBrand(false);
                                setTumblrBrandedContent(false);
                              }
                            }}
                          />
                        </div>

                        {tumblrContentDisclosure && (
                          <div className="space-y-3 pt-4 animate-in slide-in-from-top-2 duration-300">
                            <div className="flex items-center space-x-3 py-1">
                              <Checkbox
                                id="tumblr-your-brand"
                                checked={tumblrYourBrand}
                                onCheckedChange={(v) => setTumblrYourBrand(!!v)}
                              />
                              <div className="space-y-0.5 leading-none">
                                <Label htmlFor="tumblr-your-brand" className="text-sm font-bold text-foreground cursor-pointer">Your Brand</Label>
                                <p className="text-xs text-muted-foreground">You are promoting yourself or your own business</p>
                              </div>
                            </div>

                            <div className="flex items-center space-x-3 py-1">
                              <Checkbox
                                id="tumblr-branded-content"
                                checked={tumblrBrandedContent}
                                onCheckedChange={(v) => setTumblrBrandedContent(!!v)}
                              />
                              <div className="space-y-0.5 leading-none">
                                <Label htmlFor="tumblr-branded-content" className="text-sm font-bold text-foreground cursor-pointer">Branded Content</Label>
                                <p className="text-xs text-muted-foreground">You are promoting another brand or a third party</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {tumblrContentDisclosure && (
                        <div className="space-y-3">
                          {!tumblrYourBrand && !tumblrBrandedContent && (
                            <div className="flex items-center gap-3 bg-red-500/10 border border-red-500/20 p-3 rounded-xl text-red-400 animate-pulse">
                              <AlertTriangle className="h-5 w-5" />
                              <p className="text-xs font-bold">You need to indicate if your content promotes yourself, a third party, or both.</p>
                            </div>
                          )}
                          {(tumblrYourBrand || tumblrBrandedContent) && (
                            <div className="flex items-center gap-3 bg-primary/10 border border-primary/20 p-3 rounded-xl text-primary">
                              <Info className="h-5 w-5" />
                              <p className="text-xs font-bold">
                                {tumblrBrandedContent
                                  ? "Your photo/video will be labeled as 'Paid partnership'"
                                  : "Your photo/video will be labeled as 'Promotional content'"}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  */}

                  <Separator className="bg-card" />

                  <div className="p-5 bg-background rounded-3xl border border-border flex flex-col xl:flex-row gap-6 items-start justify-between shadow-2xl">
                    <div className="flex-1 space-y-4 w-full">
                      <div className="flex items-center gap-2 text-muted-foreground text-sm font-medium">
                        <Clock className="h-4 w-4 text-primary" />
                        Publication Timing
                      </div>

                      <RadioGroup
                        defaultValue="now"
                        value={postMode}
                        onValueChange={(v: any) => setPostMode(v)}
                        disabled={isReadMode}
                        className="flex flex-row gap-6"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="now" id="now" className="border-border text-primary" />
                          <Label htmlFor="now" className="text-foreground cursor-pointer text-xs font-bold uppercase tracking-wider">Post Now</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="schedule" id="schedule" className="border-border text-primary" />
                          <Label htmlFor="schedule" className="text-foreground cursor-pointer text-xs font-bold uppercase tracking-wider">Schedule Post</Label>
                        </div>
                      </RadioGroup>

                      {postMode === 'schedule' && (
                        <div className="pt-2 animate-in slide-in-from-top-2 duration-200">
                          <Input
                            type="datetime-local"
                            value={scheduledAt}
                            onChange={(e) => setScheduledAt(e.target.value)}
                            disabled={isReadMode}
                            min={moment().format('YYYY-MM-DDTHH:mm')}
                            max={moment().add(2, 'months').format('YYYY-MM-DDTHH:mm')}
                            className={cn(
                              "bg-card border-border text-foreground focus:ring-primary h-11 rounded-xl font-bold",
                              moment(scheduledAt).isAfter(moment().add(2, 'months')) && "border-red-500 ring-1 ring-red-500"
                            )}
                          />
                          {moment(scheduledAt).isAfter(moment().add(2, 'months')) && (
                            <p className="text-[10px] text-red-400 font-bold mt-1.5 flex items-center gap-1.5 uppercase tracking-tight">
                              <AlertCircle className="h-3 w-3" />
                              Maximum scheduling limit is 2 months from now
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="shrink-0 pt-6 xl:pt-2 flex flex-col items-end gap-3 min-w-[240px] w-full xl:w-auto">
                      {/* Media blocking issues warning */}
                      {mediaBlockingIssues.hasIssues && (
                        <div className="w-full animate-in fade-in slide-in-from-top-2 duration-300">
                          <div className="bg-red-950/40 border border-red-500/50 rounded-lg p-3 flex items-start gap-2">
                            <AlertTriangle className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              <p className="text-xs font-semibold text-red-300 mb-0.5">Media Configuration Issue</p>
                              <p className="text-[11px] text-red-400 leading-snug">{mediaBlockingIssues.message}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {hasTikTokSelected && (
                        <div className="flex flex-col items-end gap-1 mb-1">
                          <span className="text-[9px] text-primary/60 font-bold uppercase tracking-tighter">
                            TikTok: Content may take a few minutes to process
                          </span>
                        </div>
                      )}
                      <TooltipProvider>
                        <Tooltip open={tiktokContentDisclosure && !tiktokYourBrand && !tiktokBrandedContent && isUploadDisabled ? undefined : false}>
                          <TooltipTrigger asChild>
                            <div className="w-full">
                              <UploadButton
                                uploading={uploading}
                                disabled={isUploadDisabled}
                                onClick={() => {
                                  if (hasTikTokSelected) {
                                    setShowTikTokConfirmation(true);
                                  } else {
                                    handleUpload();
                                  }
                                }}
                                onDisabledClick={() => {
                                  if (selectedPlatforms.length === 0) {
                                    setShouldHighlightSocials(true);
                                    setTimeout(() => setShouldHighlightSocials(false), 2000);
                                  }
                                }}
                                isDryRun={debugMode}
                                disabledReasons={getDisabledReasons()}
                              />
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="top" align="end" className="bg-red-950 border-red-500/30 text-red-200 text-xs p-3 max-w-[280px] shadow-2xl z-[100]">
                            {tiktokContentDisclosure && !tiktokYourBrand && !tiktokBrandedContent ? "You need to indicate if your content promotes yourself, a third party, or both." : "Fix validation errors before publishing"}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </div>

                  {/* Smart YouTube Fallback — project-level (only for video, not images) */}
                  {youtubeAccountsForProject.length > 0 && !isReadMode && !(localFile ? localFile.type.startsWith('image/') : (mediaPath?.match(/\.(jpg|jpeg|png|gif|webp)$/i) !== null)) && (
                    <div className="p-4 bg-background rounded-2xl border border-border shadow-lg animate-in fade-in slide-in-from-bottom-2 duration-300">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="bg-red-500/10 p-2 rounded-lg">
                            <Youtube className="h-4 w-4 text-red-500" />
                          </div>
                          <div>
                            <p className="text-xs font-bold text-foreground">Smart YouTube Fallback</p>
                            <p className="text-[10px] text-muted-foreground leading-tight mt-0.5">
                              Upload to YouTube as Unlisted, share link instead of compressing
                            </p>
                          </div>
                        </div>
                        <Switch
                          checked={ytFallbackEnabled}
                          onCheckedChange={(checked) => handleYtFallbackChange(checked)}
                          className="data-[state=checked]:bg-red-500"
                        />
                      </div>
                      {ytFallbackEnabled && youtubeAccountsForProject.length > 1 && (
                        <div className="mt-3 pl-11">
                          <select
                            value={ytFallbackAccountId || ''}
                            onChange={(e) => handleYtFallbackChange(true, e.target.value)}
                            className="w-full bg-card border border-border text-foreground text-xs rounded-lg px-3 py-2 focus:ring-1 focus:ring-red-500 focus:border-red-500"
                          >
                            {youtubeAccountsForProject.map((acc: any) => (
                              <option key={acc.id} value={acc.id}>{acc.name}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  )}

                  {selectedPlatforms.length > 0 && (videoDuration || fileSize || localFile) && (
                    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                      <PlatformValidations
                        selectedPlatforms={selectedPlatforms
                          .map(accountId => {
                            const account = activeProject?.socialAccounts?.find(sa => sa.socialAccount.id === accountId);
                            return account?.socialAccount.platform;
                          })
                          .filter((platform): platform is string => platform !== undefined)}
                        videoDuration={videoDuration}
                        selectedDuration={((videoEndTime ?? videoDuration) || 0) - (videoStartTime ?? 0)}
                        fileSize={fileSize}
                        isImage={localFile ? localFile.type.startsWith('image/') : (mediaPath?.match(/\.(jpg|jpeg|png|gif|webp)$/i) !== null)}
                        addEndLogo={globalState.addEndLogo}
                        youtubeFallbackEnabled={ytFallbackEnabled}
                        onValidationChange={(err, warn) => { setHasValidationErrors(err); }}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div className="lg:col-span-5 flex gap-6 items-start h-full pt-4">
                  <div className="shrink-0">
                    {(() => {
                      // If post has schedules, use them; otherwise use project's social accounts
                      const hasSchedules = currentPost?.schedules && currentPost.schedules.length > 0;
                      const accounts = hasSchedules
                        ? (currentPost?.schedules || []).map(s => ({
                          id: s.id,
                          name: s.socialAccount?.name || 'Account',
                          platform: s.socialAccount?.platform || 'unknown',
                          handle: s.socialAccount?.handle,
                          avatarUrl: s.socialAccount?.avatarUrl,
                          isValid: true,
                          status: s.status
                        }))
                        : (activeProject?.socialAccounts?.map(sa => ({
                          id: sa.socialAccount.id,
                          name: sa.socialAccount.name,
                          platform: sa.socialAccount.platform,
                          handle: sa.socialAccount.handle,
                          avatarUrl: sa.socialAccount.avatarUrl,
                          isValid: sa.socialAccount.isValid !== false,
                          status: undefined
                        })) || []);

                      const accountCount = accounts.length;
                      const shouldShowAggregate = accountCount > 1;

                      return (
                        <SocialMediaSelector
                          mode="select"
                          accounts={accounts}
                          selectedIds={hasSchedules
                            ? (selectedScheduleId === 'all' ? (currentPost?.schedules || []).map(s => s.id) : [selectedScheduleId])
                            : (selectedScheduleId === 'all' ? accounts.map(a => a.id) : [selectedScheduleId])
                          }
                          onToggle={(id) => setSelectedScheduleId(id)}
                          showConnectButton={false}
                          showAggregateButton={shouldShowAggregate}
                          isAggregateSelected={selectedScheduleId === 'all'}
                          onAggregateClick={() => setSelectedScheduleId('all')}
                        />
                      );
                    })()}
                  </div>
                  <div className="flex-1 space-y-4">
                    <Card className="bg-card border-border shadow-xl overflow-hidden">
                      <CardHeader className="p-5 border-b border-border/50 flex flex-row items-center justify-between bg-muted/20">
                        <div className="flex flex-col">
                          <CardTitle className="text-sm font-black text-white uppercase tracking-widest flex items-center gap-2">
                            <TrendingUp className="h-4 w-4 text-rose-500" />
                            {(() => {
                              const hasSchedules = currentPost?.schedules && currentPost.schedules.length > 0;
                              const accountCount = hasSchedules
                                ? (currentPost?.schedules?.length || 0)
                                : (activeProject?.socialAccounts?.length || 0);

                              if (selectedScheduleId === 'all' && accountCount > 1) {
                                return 'Aggregate Engagement';
                              }
                              return 'Platform Metrics';
                            })()}
                            {(() => {
                              const s = selectedScheduleId === 'all'
                                ? (currentPost?.schedules?.length === 1 ? currentPost.schedules[0] : null)
                                : currentPost?.schedules?.find(x => x.id === selectedScheduleId);
                              if (s?.isBanger) return <Badge className="bg-amber-500 text-white border-none text-[8px] h-4 gap-1 animate-bounce"><Flame className="h-2 w-2 fill-white" /> BANGER</Badge>;
                              return null;
                            })()}
                          </CardTitle>
                          {(() => {
                            const hasSchedules = currentPost?.schedules && currentPost.schedules.length > 0;
                            if (!hasSchedules) {
                              const selectedAccount = selectedScheduleId !== 'all'
                                ? activeProject?.socialAccounts?.find(sa => sa.socialAccount.id === selectedScheduleId)
                                : null;
                              if (selectedAccount) {
                                const fullLabel = `${selectedAccount.socialAccount.name} (${selectedAccount.socialAccount.handle?.startsWith('@') ? selectedAccount.socialAccount.handle : `@${selectedAccount.socialAccount.handle}`})`;
                                const needsTruncate = fullLabel.length > 20;
                                return (
                                  <TooltipProvider delayDuration={0}>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span className="text-[9px] text-muted-foreground font-bold uppercase mt-0.5 block max-w-[160px] truncate cursor-default">
                                          {needsTruncate ? fullLabel.slice(0, 20) + '...' : fullLabel}
                                        </span>
                                      </TooltipTrigger>
                                      {needsTruncate && (
                                        <TooltipContent side="bottom" className="bg-background border-border text-foreground text-xs">
                                          {fullLabel}
                                        </TooltipContent>
                                      )}
                                    </Tooltip>
                                  </TooltipProvider>
                                );
                              }
                              return null;
                            }

                            if (selectedScheduleId !== 'all' || currentPost?.schedules?.length === 1) {
                              const s = selectedScheduleId === 'all'
                                ? currentPost?.schedules?.[0]
                                : currentPost?.schedules?.find(x => x.id === selectedScheduleId);

                              const handleText = s?.socialAccount?.handle ? (s.socialAccount.handle.startsWith('@') ? s.socialAccount.handle : `@${s.socialAccount.handle}`) : '';
                              const fullLabel2 = `${s?.socialAccount?.name || ''} (${handleText})`;
                              const needsTruncate2 = fullLabel2.length > 20;
                              return (
                                <div className="flex items-center gap-2 mt-1">
                                  {s?.socialAccount?.avatarUrl && (
                                    <Avatar className="h-4 w-4 border border-border">
                                      <AvatarImage src={s.socialAccount.avatarUrl} />
                                      <AvatarFallback className="text-[6px]">{s.socialAccount.name.charAt(0)}</AvatarFallback>
                                    </Avatar>
                                  )}
                                  <TooltipProvider delayDuration={0}>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span className="text-[9px] text-muted-foreground font-bold uppercase block max-w-[160px] truncate cursor-default">
                                          {needsTruncate2 ? fullLabel2.slice(0, 20) + '...' : fullLabel2}
                                        </span>
                                      </TooltipTrigger>
                                      {needsTruncate2 && (
                                        <TooltipContent side="bottom" className="bg-background border-border text-foreground text-xs">
                                          {fullLabel2}
                                        </TooltipContent>
                                      )}
                                    </Tooltip>
                                  </TooltipProvider>
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>
                        <div className="flex items-center gap-2">
                          {(() => {
                            const s = selectedScheduleId === 'all'
                              ? (currentPost?.schedules?.length === 1 ? currentPost.schedules[0] : null)
                              : currentPost?.schedules?.find(x => x.id === selectedScheduleId);
                            if (!s || !s.externalPostId) return null;

                            let url = '';
                            let noLinkReason = '';
                            let platform = s.socialAccount?.platform?.toLowerCase() || '';
                            const handle = platform.includes('tiktok')
                              ? (s.socialAccount?.handle || s.socialAccount?.name || '').replace(/\s+/g, '').toLowerCase()
                              : (s.socialAccount?.handle || s.socialAccount?.name || '');

                            // Robust platform determination if socialAccount is missing from schedule
                            if (!platform && activeProject?.socialAccounts) {
                              const account = activeProject.socialAccounts.find(sa => sa.socialAccount.id === s.socialAccountId);
                              if (account) platform = account.socialAccount.platform.toLowerCase();
                            }

                            // Check if we have a permalink in the result object
                            try {
                              const resultObj = s.result ? JSON.parse(s.result) : null;
                              if (resultObj?.data?.permalink) url = resultObj.data.permalink;
                              else if (resultObj?.permalink) url = resultObj.permalink;
                              else if (resultObj?.url) url = resultObj.url;

                              // Legacy fix: Convert old tumblr subdomain links to modern format
                              if (url && url.includes('.tumblr.com/post/')) {
                                const match = url.match(/https?:\/\/([^.]+)\.tumblr\.com\/post\/(\d+)/);
                                if (match) {
                                  url = `https://www.tumblr.com/${match[1]}/${match[2]}`;
                                }
                              }

                              // Discord "undefined" guildId fix
                              if (url && url.includes('/channels/undefined/')) {
                                // If it's a discord channel link with undefined, we try to fix it regardless of what 'platform' says
                                // as it's almost certainly a Discord link.
                                let guildId = s.socialAccount?.refreshToken || '';

                                // Fallback: search in activeProject social accounts if not in schedule
                                if (!guildId && activeProject?.socialAccounts) {
                                  const account = activeProject.socialAccounts.find(sa => sa.socialAccount.id === s.socialAccountId);
                                  if (account) guildId = account.socialAccount.refreshToken || '';
                                }

                                if (guildId) {
                                  url = url.replace('/channels/undefined/', `/channels/${guildId}/`);
                                }
                              }
                            } catch (e) { }

                            if (!url) {
                              if (platform.includes('twitter') || platform === 'x' || platform === 'x-es') {
                                url = `https://x.com/i/status/${s.externalPostId}`;
                              } else if (platform.includes('tiktok')) {
                                // If it's a publish_id (starts with v_pub_file), we can't build a direct link yet
                                // as the video is still being processed by TikTok.
                                if (s.externalPostId?.startsWith('v_pub_file')) {
                                  noLinkReason = "TikTok is still processing this video. The final link will be available in a few minutes.";
                                  url = ""; // Clear URL to trigger "Link Unavailable"
                                } else {
                                  url = `https://www.tiktok.com/@${handle.replace('@', '')}/video/${s.externalPostId}`;
                                }
                              } else if (platform.includes('bluesky')) {
                                let blueskyId = s.externalPostId || '';
                                if (blueskyId.startsWith('at://')) {
                                  const parts = blueskyId.split('/');
                                  blueskyId = parts[parts.length - 1];
                                }
                                url = `https://bsky.app/profile/${handle}/post/${blueskyId}`;
                              } else if (platform.includes('instagram')) {
                                url = `https://www.instagram.com/reels/${s.externalPostId}/`;
                              } else if (platform.includes('telegram')) {
                                const messageId = s.externalPostId;
                                if (messageId) {
                                  if (handle && handle.startsWith('@')) {
                                    url = `https://t.me/${handle.substring(1)}/${messageId}`;
                                  } else if (handle && !handle.includes(' ') && isNaN(Number(handle))) {
                                    url = `https://t.me/${handle}/${messageId}`;
                                  } else {
                                    // Private channel or numeric ID
                                    const chatId = s.socialAccount?.externalId || '';
                                    if (chatId) {
                                      if (chatId.startsWith('-100')) {
                                        const numericChatId = chatId.replace('-100', '');
                                        url = `https://t.me/c/${numericChatId}/${messageId}`;
                                      } else {
                                        noLinkReason = "Telegram basic groups don't support direct message links. Convert to a Supergroup to enable them.";
                                      }
                                    }
                                  }
                                }
                              } else if (platform.includes('discord')) {
                                // Reconstruct Discord URL if missing from result object
                                const messageId = s.externalPostId;
                                if (messageId) {
                                  let guildId = s.socialAccount?.refreshToken || '';
                                  if (!guildId && activeProject?.socialAccounts) {
                                    const account = activeProject.socialAccounts.find(sa => sa.socialAccount.id === s.socialAccountId);
                                    if (account) guildId = account.socialAccount.refreshToken || '';
                                  }

                                  const channelId = s.socialAccount?.externalId || '';

                                  if (guildId && channelId) {
                                    url = `https://discord.com/channels/${guildId}/${channelId}/${messageId}`;
                                  }
                                }
                              } else if (platform.includes('tumblr')) {
                                try {
                                  const resultObj = s.result ? JSON.parse(s.result) : null;
                                  if (resultObj?.url) url = resultObj.url;
                                  else {
                                    const tumblrId = resultObj?.id || s.externalPostId;
                                    if (tumblrId) {
                                      const cleanHandle = handle.replace('@', '');
                                      url = `https://www.tumblr.com/${cleanHandle}/${tumblrId}`;
                                    }
                                  }
                                } catch (e) { }
                              } else if (platform.includes('threads')) {
                                // Direct ID-based numeric links often redirect to profile.
                                // We MUST use shortcodes obtained from the API (via result.permalink).
                                // If we don't have a permalink yet, we don't show a potentially broken link.
                                return null;
                              }
                            }

                            if (!url) {
                              if (noLinkReason) {
                                return (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div className="cursor-help">
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-8 px-3 text-[10px] font-black uppercase tracking-widest text-muted-foreground gap-2 border border-border rounded-lg opacity-50"
                                            disabled
                                          >
                                            <ExternalLink className="h-3.5 w-3.5" />
                                            Link Unavailable
                                          </Button>
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent side="top" className="bg-muted border-border text-foreground text-xs p-3 max-w-[250px] z-[100]">
                                        <div className="space-y-1">
                                          <p className="font-bold text-amber-400">Why is the link missing?</p>
                                          <p>{noLinkReason}</p>
                                        </div>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                );
                              }
                              return null;
                            }

                            const buttonText = (platform.includes('telegram') || platform.includes('discord'))
                              ? 'view message'
                              : platform.includes('youtube')
                                ? 'view video'
                                : 'view post';

                            return (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 px-3 text-[10px] font-black uppercase tracking-widest text-primary hover:text-primary/80 hover:bg-primary/10 gap-2 border border-primary/20 rounded-lg"
                                asChild
                              >
                                <a href={url} target="_blank" rel="noopener noreferrer">
                                  <ExternalLink className="h-3.5 w-3.5" />
                                  {buttonText}
                                </a>
                              </Button>
                            );
                          })()}

                          {currentPost?.schedules && currentPost.schedules.length > 0 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-muted-foreground hover:text-primary hover:bg-primary/10"
                              onClick={handleRefreshStats}
                              disabled={refreshingStats}
                              title={user?.isAdmin ? "Refresh analytics (Admin)" : "Refresh analytics"}
                            >
                              <RefreshCw className={cn("h-4 w-4", refreshingStats && "animate-spin")} />
                            </Button>
                          )}
                          {(() => {
                            const schedules = selectedScheduleId === 'all'
                              ? (currentPost?.schedules || [])
                              : (currentPost?.schedules?.filter(s => s.id === selectedScheduleId) || []);

                            const latestUpdate = schedules
                              .map(s => s.lastStatsUpdate)
                              .filter(Boolean)
                              .sort((a, b) => new Date(b!).getTime() - new Date(a!).getTime())[0];

                            if (!latestUpdate) return null;

                            return (
                              <div className="flex items-center gap-1.5">
                                <Clock className="h-3 w-3 text-muted-foreground" />
                                <span className="text-[9px] font-black text-muted-foreground uppercase tracking-wider">
                                  Updated {moment(latestUpdate).fromNow()}
                                </span>
                              </div>
                            );
                          })()}
                        </div>
                      </CardHeader>
                      <CardContent className="p-6 space-y-6">
                        {/* Deleted Status Alert */}
                        {(() => {
                          const s = selectedScheduleId === 'all'
                            ? (currentPost?.schedules?.length === 1 ? currentPost.schedules[0] : null)
                            : currentPost?.schedules?.find(x => x.id === selectedScheduleId);

                          if (s?.status !== 'DELETED') return null;

                          return (
                            <div className="bg-rose-500/10 border border-rose-500/30 rounded-2xl p-4 space-y-3 animate-in slide-in-from-top-2 duration-300">
                              <div className="flex items-start gap-3">
                                <div className="p-2 bg-rose-500/20 rounded-xl">
                                  <Trash2 className="h-4 w-4 text-rose-500" />
                                </div>
                                <div className="flex-1 space-y-1">
                                  <h4 className="text-xs font-black text-rose-500 uppercase tracking-widest">
                                    Post Deleted
                                  </h4>
                                  <p className="text-[10px] text-muted-foreground leading-relaxed">
                                    This post is no longer available on the social media platform.
                                  </p>
                                </div>
                              </div>
                              {s?.externalPostId && (
                                <div className="flex items-center gap-2 pt-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-[10px] h-7 border-primary/30 text-primary hover:bg-primary/10 hover:text-primary/80 font-bold"
                                    onClick={async () => {
                                      try {
                                        const response = await axios.post(
                                          `${API_BASE_URL}/posts/schedules/${s.id}/restore`,
                                          {},
                                          { headers: { Authorization: `Bearer ${token}` } }
                                        );
                                        toast.success("Post restored successfully!");
                                        // Refresh the post data
                                        if (currentPost?.id) {
                                          const updatedPost = await axios.get(`${API_BASE_URL}/posts/${currentPost.id}`, {
                                            headers: { Authorization: `Bearer ${token}` }
                                          });
                                          setCurrentPost(updatedPost.data);
                                        }
                                      } catch (err: any) {
                                        console.error("Error restoring post:", err);
                                        toast.error(err.response?.data?.error || "Failed to restore post");
                                      }
                                    }}
                                  >
                                    <RefreshCw className="h-3 w-3 mr-1" />
                                    Post still exists? Restore it
                                  </Button>
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* Failed Status Alert */}
                        {(() => {
                          const failedSchedules = selectedScheduleId === 'all'
                            ? (currentPost?.schedules?.filter(s => s.status === 'FAILED') || [])
                            : (currentPost?.schedules?.filter(s => s.id === selectedScheduleId && s.status === 'FAILED') || []);

                          if (failedSchedules.length === 0) return null;

                          return (
                            <div className="bg-rose-500/10 border border-rose-500/30 rounded-2xl p-4 space-y-3 animate-in slide-in-from-top-2 duration-300">
                              <div className="flex items-start gap-3">
                                <div className="p-2 bg-rose-500/20 rounded-xl">
                                  <AlertTriangle className="h-4 w-4 text-rose-500" />
                                </div>
                                <div className="flex-1 space-y-1">
                                  <h4 className="text-xs font-black text-rose-500 uppercase tracking-widest">
                                    {selectedScheduleId === 'all' ? 'Some Deliveries Failed' : 'Delivery Failed'}
                                  </h4>
                                  <p className="text-[10px] text-muted-foreground leading-relaxed">
                                    {selectedScheduleId === 'all'
                                      ? `This post could not be delivered to ${failedSchedules.length} platform(s).`
                                      : `Error: ${failedSchedules[0].result || 'Unknown API error'}`}
                                  </p>
                                </div>
                              </div>

                              {selectedScheduleId !== 'all' ? (
                                <Button
                                  variant="default"
                                  size="sm"
                                  onClick={() => handleResend(selectedScheduleId)}
                                  disabled={!!resendingScheduleId}
                                  className="w-full bg-rose-600 hover:bg-rose-500 text-white font-black uppercase tracking-widest text-[10px] h-8 rounded-xl shadow-lg shadow-rose-600/20 gap-2"
                                >
                                  {resendingScheduleId === selectedScheduleId ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  ) : (
                                    <Send className="h-3.5 w-3.5" />
                                  )}
                                  Resend Now (Synchronous)
                                </Button>
                              ) : (
                                <div className="space-y-2">
                                  {failedSchedules.map(fs => (
                                    <Button
                                      key={fs.id}
                                      variant="default"
                                      size="sm"
                                      onClick={() => handleResend(fs.id)}
                                      disabled={!!resendingScheduleId}
                                      className="w-full bg-rose-600 hover:bg-rose-500 text-white font-black uppercase tracking-widest text-[9px] h-8 rounded-xl shadow-lg shadow-rose-600/20 gap-2"
                                    >
                                      {resendingScheduleId === fs.id ? (
                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                      ) : (
                                        <div className="flex items-center gap-2">
                                          {getPlatformIcon(fs.socialAccount?.platform || '', "h-3 w-3")}
                                          <Send className="h-3 w-3" />
                                        </div>
                                      )}
                                      Resend on {fs.socialAccount?.name}
                                    </Button>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* Top Platforms Summary (Only in Aggregate Mode) - MOVED TO TOP */}
                        {(() => {
                          if (selectedScheduleId !== 'all' || !currentPost?.schedules || currentPost.schedules.length <= 1) return null;

                          return (
                            <div className="space-y-3 pb-4 border-b border-border/50">
                              <Label className="text-[9px] font-black text-muted-foreground uppercase tracking-widest px-1">Platform Performance Summary</Label>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                {[...currentPost.schedules]
                                  .sort((a, b) => ((b.views || 0) + (b.likes || 0) * 10) - ((a.views || 0) + (a.likes || 0) * 10))
                                  .map((s, idx) => {
                                    const total = (s.views || 0) + (s.likes || 0) + (s.comments || 0) + (s.shares || 0);
                                    if (total === 0) return null;
                                    const platform = s.socialAccount?.platform || '';
                                    const viewsSupported = isMetricSupported(platform, 'views', s);
                                    const likesSupported = isMetricSupported(platform, 'likes', s);

                                    return (
                                      <div key={s.id} className="flex items-center gap-2.5 p-2 bg-background/40 rounded-xl border border-border/50 hover:bg-background/60 transition-colors cursor-pointer group" onClick={() => setSelectedScheduleId(s.id)}>
                                        <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center border border-border group-hover:border-primary/50 transition-colors shrink-0">
                                          {getPlatformIcon(platform, "h-4 w-4")}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center justify-between gap-1.5">
                                            <div className={cn("flex items-center gap-1 min-w-0", !viewsSupported && "opacity-20")}>
                                              <Eye className="h-2.5 w-2.5 text-primary" />
                                              <span className="text-[10px] font-black text-primary truncate">{viewsSupported ? (s.views || 0).toLocaleString() : '—'}</span>
                                            </div>
                                            <div className={cn("flex items-center gap-1 min-w-0", !likesSupported && "opacity-20")}>
                                              <Heart className="h-2.5 w-2.5 text-rose-500" />
                                              <span className="text-[10px] font-black text-rose-500 truncate">{likesSupported ? (s.likes || 0).toLocaleString() : '—'}</span>
                                            </div>
                                          </div>
                                          <div className="text-[8px] text-muted-foreground font-mono truncate opacity-60">{s.socialAccount?.handle ? (s.socialAccount.handle.startsWith('@') ? s.socialAccount.handle : `@${s.socialAccount.handle}`) : ''}</div>
                                        </div>
                                        <ChevronRight className="h-3 w-3 text-muted-foreground group-hover:text-primary transition-colors" />
                                      </div>
                                    );
                                  })}
                              </div>
                            </div>
                          );
                        })()}

                        {/* Metric Grid */}
                        {(() => {
                          const hasSchedules = currentPost?.schedules && currentPost.schedules.length > 0;

                          if (!hasSchedules) {
                            return (
                              <div className="bg-muted/50 border border-border rounded-2xl p-8 text-center">
                                <div className="flex flex-col items-center gap-3">
                                  <div className="p-3 bg-card/50 rounded-full">
                                    <TrendingUp className="h-6 w-6 text-muted-foreground" />
                                  </div>
                                  <div>
                                    <p className="text-sm font-black text-muted-foreground uppercase tracking-widest mb-1">No Analytics Available</p>
                                    <p className="text-xs text-muted-foreground leading-relaxed">
                                      This post has not been published to any social media platform yet.
                                    </p>
                                  </div>
                                </div>
                              </div>
                            );
                          }

                          const schedules = selectedScheduleId === 'all'
                            ? (currentPost?.schedules || [])
                            : (currentPost?.schedules?.filter(s => s.id === selectedScheduleId) || []);

                          const stats = {
                            likes: schedules.reduce((acc, s) => acc + (s.likes || 0), 0),
                            comments: schedules.reduce((acc, s) => acc + (s.comments || 0), 0),
                            shares: schedules.reduce((acc, s) => acc + (s.shares || 0), 0),
                            views: schedules.reduce((acc, s) => acc + (s.views || 0), 0),
                          };

                          const currentPlatform = selectedScheduleId !== 'all'
                            ? currentPost?.schedules?.find(s => s.id === selectedScheduleId)?.socialAccount?.platform || ''
                            : '';

                          const MetricCard = ({ icon: Icon, value, label, color, metric }: { icon: any, value: number, label: string, color: string, metric: 'likes' | 'comments' | 'shares' | 'views' }) => {
                            const schedule = selectedScheduleId !== 'all' ? currentPost?.schedules?.find(s => s.id === selectedScheduleId) : undefined;
                            const supported = selectedScheduleId === 'all' || isMetricSupported(currentPlatform, metric, schedule);
                            const displayLabel = selectedScheduleId === 'all' ? label : getMetricLabel(currentPlatform, metric);
                            const unsupportedReason = !supported ? getMetricUnsupportedReason(currentPlatform, metric, schedule) : null;

                            const card = (
                              <div className={cn(
                                "bg-background p-2.5 rounded-xl border border-border/50 text-center transition-all",
                                !supported && "opacity-30 grayscale grayscale-[0.5] border-slate-900",
                                unsupportedReason && "cursor-help"
                              )}>
                                <Icon className={cn("h-3.5 w-3.5 mx-auto mb-1", color, !supported && "text-muted-foreground")} />
                                <div className={cn("text-base font-black leading-none", !supported ? "text-muted-foreground" : "text-foreground")}>
                                  {supported ? value.toLocaleString() : '—'}
                                </div>
                                <div className="text-[8px] uppercase font-black tracking-tighter text-muted-foreground mt-1">
                                  {displayLabel}
                                  {!supported && <span className="block text-[6px] opacity-50 tracking-normal mt-0.5">N/A</span>}
                                </div>
                              </div>
                            );

                            if (unsupportedReason) {
                              return (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      {card}
                                    </TooltipTrigger>
                                    <TooltipContent className="bg-muted border-border text-[10px] p-2 max-w-[200px]">
                                      {unsupportedReason}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              );
                            }

                            return card;
                          };

                          return (
                            <div className="grid grid-cols-4 gap-3">
                              <MetricCard icon={Heart} value={stats.likes} label="Likes" color="text-rose-500" metric="likes" />
                              <MetricCard icon={MessageCircle} value={stats.comments} label="Comments" color="text-sky-500" metric="comments" />
                              <MetricCard icon={Repeat2} value={stats.shares} label="Shares" color="text-emerald-500" metric="shares" />
                              <MetricCard icon={Eye} value={stats.views} label="Views" color="text-primary" metric="views" />
                            </div>
                          );
                        })()}

                        {/* Graph Row with LPV on the left */}
                        <div className="flex flex-col lg:flex-row gap-4 items-stretch">
                          {/* Like per View % Panel (Left of graph) */}
                          <div className="shrink-0 flex flex-col items-center justify-center p-4 bg-background/40 rounded-2xl border border-border/50 min-w-[100px] space-y-1">
                            {(() => {
                              const schedules = selectedScheduleId === 'all'
                                ? (currentPost?.schedules || [])
                                : (currentPost?.schedules?.filter(s => s.id === selectedScheduleId) || []);

                              const currentPlatform = selectedScheduleId !== 'all'
                                ? currentPost?.schedules?.find(s => s.id === selectedScheduleId)?.socialAccount?.platform || ''
                                : '';

                              const views = schedules.reduce((acc, s) => acc + (s.views || 0), 0);
                              const likes = schedules.reduce((acc, s) => acc + (s.likes || 0), 0);
                              const lpv = views > 0 ? (likes / views) * 100 : 0;

                              return (
                                <>
                                  <div
                                    className="text-2xl font-black text-rose-500 tracking-tighter cursor-help"
                                    title={currentPlatform.includes('telegram') ? "% of Reactions per impression" : "% of Likes per impression"}
                                  >
                                    {lpv.toFixed(1)}%
                                  </div>
                                  <div className="text-[8px] font-black text-muted-foreground uppercase tracking-widest text-center">
                                    {currentPlatform.includes('telegram') ? 'RPV Rate' : 'LPV Rate'}
                                  </div>
                                </>
                              );
                            })()}
                          </div>

                          {/* Graph (Right) */}
                          <div className="flex-1">
                            <EngagementGraph schedules={selectedScheduleId === 'all' ? (currentPost?.schedules || []) : (currentPost?.schedules?.filter(s => s.id === selectedScheduleId) || [])} />
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {(() => {
                      const filteredSchedules = selectedScheduleId === 'all'
                        ? (currentPost?.schedules || [])
                        : (currentPost?.schedules?.filter(s => s.id === selectedScheduleId) || []);

                      const comments = filteredSchedules
                        .flatMap(s => (s.commentsList || []).map(c => ({ ...c, platform: s.socialAccount?.platform })))
;

                      if (comments.length === 0) return null;

                      return (
                        <Card className="bg-card border-border shadow-xl flex flex-col min-h-[300px]">
                          <CardHeader className="p-5 border-b border-border/50 flex flex-row items-center justify-between">
                            <CardTitle className="text-sm font-black text-white uppercase tracking-widest flex items-center gap-2">
                              <MessageSquareReply className="h-4 w-4 text-primary" />
                              Comments
                            </CardTitle>
                            <div className="flex items-center gap-1.5">
                              <div className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
                              <span className="text-[9px] font-black text-muted-foreground uppercase tracking-wider">Feed</span>
                            </div>
                          </CardHeader>
                          <CardContent className="p-0 overflow-hidden">
                            <div className="h-[300px] overflow-y-auto">
                              <div className="divide-y divide-border/50">
                                {comments.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).map((comment: any) => (
                                  <div key={comment.id} className="p-4 hover:bg-card/30 transition-colors group relative">
                                    <div className="flex gap-3">
                                      <Avatar className="h-9 w-9 ring-1 ring-border shrink-0">
                                        <AvatarImage src={comment.authorAvatar} />
                                        <AvatarFallback className="bg-card text-[10px] font-bold text-muted-foreground">
                                          {comment.authorName?.substring(0, 2).toUpperCase() || '??'}
                                        </AvatarFallback>
                                      </Avatar>
                                      <div className="flex-1 space-y-1">
                                        <div className="flex items-center justify-between">
                                          <div className="flex items-center gap-2">
                                            <span className="text-[15px] font-black text-foreground">{comment.authorName}</span>
                                            {comment.authorVerified && (
                                              <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
                                            )}
                                            <a
                                              href={`https://x.com/${comment.authorHandle}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-[13px] text-muted-foreground hover:text-primary transition-colors"
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              {comment.authorHandle ? (comment.authorHandle.startsWith('@') ? comment.authorHandle : `@${comment.authorHandle}`) : ''}
                                            </a>
                                            {comment.authorFollowers > 0 && (
                                              <span className="text-[11px] font-bold text-muted-foreground bg-card px-2 py-0.5 rounded flex items-center gap-1.5">
                                                <Users className="h-3 w-3" />
                                                {formatFollowers(comment.authorFollowers)}
                                              </span>
                                            )}
                                            {selectedScheduleId === 'all' && (
                                              <div className="ml-1 opacity-50 scale-100 transform origin-left">
                                                {getPlatformIcon(comment.platform || '')}
                                              </div>
                                            )}
                                          </div>
                                          <div className="h-2 w-2 rounded-full bg-primary" />
                                        </div>
                                        <p className="text-[15px] text-foreground leading-relaxed py-1">
                                          {comment.text}
                                        </p>
                                        <div className="flex items-center justify-between pt-2">
                                          <span className="text-[11px] text-muted-foreground font-bold uppercase tracking-wide">{moment(comment.createdAt).fromNow()}</span>

                                          <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              className="h-8 px-3 text-[11px] font-black uppercase tracking-widest text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 gap-2 border border-rose-500/20"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleLikeComment(comment.id);
                                              }}
                                              disabled={likingCommentId === comment.id}
                                            >
                                              {likingCommentId === comment.id ? (
                                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                              ) : (
                                                <Heart className="h-3.5 w-3.5" />
                                              )}
                                              Like
                                            </Button>

                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              className="h-8 px-4 text-[11px] font-black uppercase tracking-widest text-primary hover:text-primary/80 hover:bg-primary/10 gap-2 border border-primary/20"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setReplyingComment(comment);
                                                setReplyText(user?.autoReplyUsername ? `${comment.authorHandle.startsWith('@') ? comment.authorHandle : `@${comment.authorHandle}`} ` : '');
                                              }}
                                            >
                                              <MessageSquareReply className="h-3.5 w-3.5" />
                                              Reply
                                            </Button>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })()}
                  </div>
                </div>
              )}

            </div>
          </ScrollArea>
        </div>

        {/* Global Progress Overlay for Generating Captions */}
        {showGenerateDialog && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm animate-in fade-in duration-300">
            <Card className="w-full max-w-sm bg-card border-border shadow-2xl">
              <CardContent className="p-8 text-center space-y-6">
                <div className="relative mx-auto w-20 h-20">
                  <Loader2 className="h-20 w-20 animate-spin text-primary" />
                  <Sparkles className="absolute inset-0 m-auto h-8 w-8 text-primary animate-pulse" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-xl font-bold text-white">{generateProgress.message}</h3>
                  <p className="text-muted-foreground text-sm">AI is analyzing your video content...</p>
                </div>
                <div className="space-y-2">
                  <Progress value={generateProgress.progress} className="h-2 bg-card" />
                  <p className="text-xs text-muted-foreground font-mono">{generateProgress.progress}%</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Status Messages at the bottom */}
        {error && (
          <div className="px-6 pb-6 pt-2 shrink-0">
            <Alert variant="destructive" className="bg-red-500/10 border-red-500/20 text-red-400">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          </div>
        )}

        <Dialog open={showTikTokConfirmation} onOpenChange={setShowTikTokConfirmation}>
          <DialogContent className="bg-background border-border text-foreground sm:max-w-md p-0 overflow-hidden">
            <div className="p-8 space-y-6">
              <div className="flex items-center gap-4">
                <div className="bg-primary/10 p-3 rounded-2xl border border-primary/20">
                  <Music2 className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <DialogTitle className="text-xl font-black text-foreground tracking-tight italic uppercase">Confirm TikTok Post</DialogTitle>
                  <p className="text-xs text-muted-foreground font-medium">You are about to publish a video to TikTok.</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="bg-muted/50 rounded-2xl p-4 border border-border flex flex-col gap-3">
                  <Label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground px-1">Target Accounts</Label>
                  <div className="space-y-2">
                    {selectedTikTokAccounts.map(account => (
                      <div key={account.socialAccount.id} className="flex items-center gap-3 bg-background/50 p-2 rounded-xl border border-border/50">
                        <div className="h-8 w-8 rounded-full overflow-hidden border border-border">
                          <SocialAvatar
                            accountId={account.socialAccount.id}
                            platform={account.socialAccount.platform}
                            name={account.socialAccount.name}
                            avatarUrl={account.socialAccount.avatarUrl}
                          />
                        </div>
                        <div className="flex flex-col">
                          <span className="text-xs font-bold text-foreground leading-none">{account.socialAccount.name}</span>
                          <span className="text-[10px] text-muted-foreground">{account.socialAccount.handle ? (account.socialAccount.handle.startsWith('@') ? account.socialAccount.handle : `@${account.socialAccount.handle}`) : 'tiktok_user'}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-amber-500/5 rounded-2xl p-4 border border-amber-500/20">
                  <div className="flex gap-3">
                    <AlertCircle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                    <div className="space-y-2">
                      <p className="text-[11px] text-amber-200/70 leading-relaxed italic">
                        By confirming, your video will be sent to TikTok.
                        <span className="block mt-1 font-bold text-amber-400">Content may take a few minutes to process before it's visible on your profile.</span>
                      </p>
                      <p className="text-[10px] text-muted-foreground font-bold border-t border-amber-500/10 pt-2">
                        {tiktokDeclaration}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter className="bg-background/50 p-6 border-t border-border gap-3">
              <Button
                variant="ghost"
                onClick={() => setShowTikTokConfirmation(false)}
                className="text-muted-foreground hover:text-foreground font-bold uppercase text-xs tracking-widest h-11"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  setShowTikTokConfirmation(false);
                  handleUpload();
                }}
                className="bg-primary hover:bg-primary text-white font-black h-11 px-8 rounded-xl shadow-lg shadow-primary/20 active:scale-[0.98] transition-all flex-1 sm:flex-none"
              >
                <CheckCircle2 className="mr-2 h-4 w-4" strokeWidth={3} />
                CONFIRM & POST
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <UploadProgressDialog
          open={showUploadProgressDialog}
          platforms={platformStatuses}
          isDryRun={debugMode}
          totalProgress={progress.progress}
          message={progress.message}
          isPublishing={isPublishing}
          onClose={() => {
            setShowUploadProgressDialog(false);
          }}
        />
        <VideoCropper
          open={showCropper}
          onClose={() => setShowCropper(false)}
          onCropComplete={(data, c, z) => {
            setCurrentTabConfig({ cropData: data, crop: c, zoom: z });
            setShowCropper(false);
          }}
          videoUrl={mediaPreview}
          videoDuration={videoDuration || 0}
          initialCrop={currentTabConfig.crop}
          initialZoom={currentTabConfig.zoom}
          initialAspect={currentTabConfig.cropData?.aspect}
          needsMobileFormat={activeTab === 'mobile'}
        />

        {/* Reply Modal */}
        <Dialog open={!!replyingComment} onOpenChange={(open) => !open && setReplyingComment(null)}>
          <DialogContent className="sm:max-w-[450px] bg-background border-border text-foreground p-0 overflow-hidden shadow-2xl">
            <DialogHeader className="p-4 border-b border-border/50 bg-muted/50">
              <DialogTitle className="text-sm font-black uppercase tracking-widest flex items-center gap-2 text-white">
                <div className="p-1.5 rounded-lg bg-primary/10 text-primary">
                  <MessageSquareReply className="h-4 w-4" />
                </div>
                Reply to {replyingComment?.authorHandle ? (replyingComment.authorHandle.startsWith('@') ? replyingComment.authorHandle : `@${replyingComment.authorHandle}`) : ''}
              </DialogTitle>
            </DialogHeader>

            <div className="p-4 space-y-4">
              {/* Original Comment Preview */}
              <div className="bg-muted/40 border border-border/50 rounded-xl p-3 relative overflow-hidden group">
                <div className="absolute top-0 left-0 w-1 h-full bg-primary/20" />
                <div className="flex gap-2.5">
                  <Avatar className="h-6 w-6 ring-1 ring-border shrink-0">
                    <AvatarImage src={replyingComment?.authorAvatar} />
                    <AvatarFallback className="bg-card text-[8px] font-bold text-muted-foreground">
                      {replyingComment?.authorName?.substring(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className="text-[10px] font-bold text-foreground truncate">{replyingComment?.authorName}</span>
                      {replyingComment?.authorVerified && (
                        <CheckCircle2 className="h-2.5 w-2.5 text-primary" />
                      )}
                      <span className="text-[9px] text-muted-foreground">{replyingComment?.authorHandle ? (replyingComment.authorHandle.startsWith('@') ? replyingComment.authorHandle : `@${replyingComment.authorHandle}`) : ''}</span>
                      {replyingComment?.authorFollowers > 0 && (
                        <span className="text-[8px] font-bold text-muted-foreground bg-card/80 px-1 rounded-sm flex items-center gap-0.5">
                          <Users className="h-2 w-2" />
                          {formatFollowers(replyingComment.authorFollowers)}
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-muted-foreground italic line-clamp-2 leading-relaxed">
                      "{replyingComment?.text}"
                    </p>
                  </div>
                </div>
              </div>

              {/* Reply Input Area */}
              <div className="space-y-3">
                <div className="relative group">
                  <Textarea
                    placeholder="Write your reply..."
                    className="min-h-[120px] bg-muted/50 border-border focus:border-primary/50 focus:ring-primary/20 text-xs resize-none placeholder:text-muted-foreground transition-all"
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                  />
                  <div className="absolute bottom-2 right-2 flex items-center gap-2">
                    <span className={cn(
                      "text-[9px] font-mono font-bold",
                      replyText.length > 280 ? "text-rose-500" : "text-muted-foreground"
                    )}>
                      {replyText.length}/280
                    </span>
                  </div>
                </div>

                {/* Media Preview / Upload Area */}
                <div className="flex items-center gap-3">
                  <input
                    type="file"
                    ref={replyImageInputRef}
                    className="hidden"
                    accept="image/*"
                    onChange={handleReplyImageChange}
                  />

                  {replyImagePreview ? (
                    <div className="relative group rounded-xl overflow-hidden ring-1 ring-border bg-muted">
                      <img
                        src={replyImagePreview}
                        alt="Reply upload"
                        className="h-20 w-20 object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                      />
                      <button
                        onClick={() => {
                          setReplyImage(null);
                          setReplyImagePreview(null);
                        }}
                        className="absolute top-1 right-1 p-1 rounded-full bg-background/80 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-rose-500"
                      >
                        <X className="h-3 w-3" />
                      </button>
                      <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent pointer-events-none" />
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-20 w-20 flex-col gap-2 bg-muted/20 border-border border-dashed hover:bg-primary/5 hover:border-primary/30 text-muted-foreground hover:text-primary transition-all rounded-xl group"
                      onClick={() => replyImageInputRef.current?.click()}
                    >
                      <div className="p-2 rounded-full bg-card/50 group-hover:bg-primary/10 transition-colors">
                        <ImageIcon className="h-4 w-4" />
                      </div>
                      <span className="text-[9px] font-black uppercase tracking-widest">Add Image</span>
                    </Button>
                  )}

                  <div className="flex-1 flex flex-col justify-center gap-1.5 px-1">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Paperclip className="h-3 w-3" />
                      <span className="text-[9px] font-bold uppercase tracking-wider">Twitter/X Specs</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground leading-tight">
                      Images only. Up to 5MB.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter className="p-4 bg-muted/50 border-t border-border/50 flex flex-row items-center justify-between sm:justify-between">
              <div className="flex items-center gap-2">
                <div className="flex items-center space-x-2 bg-background/30 px-3 py-1.5 rounded-lg border border-border/50 group cursor-pointer hover:bg-rose-500/5 hover:border-rose-500/20 transition-all" onClick={() => setReplyWithLike(!replyWithLike)}>
                  <Checkbox
                    id="like-comment"
                    checked={replyWithLike}
                    onCheckedChange={(checked) => setReplyWithLike(!!checked)}
                    className="border-border data-[state=checked]:bg-rose-500 data-[state=checked]:border-rose-500"
                  />
                  <label
                    htmlFor="like-comment"
                    className="text-[10px] font-black uppercase tracking-widest text-muted-foreground group-hover:text-rose-400 cursor-pointer flex items-center gap-1.5"
                  >
                    <Heart className={cn("h-3 w-3 transition-colors", replyWithLike ? "fill-rose-500 text-rose-500" : "text-muted-foreground")} />
                    Like comment
                  </label>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-[10px] font-black uppercase tracking-widest text-muted-foreground hover:text-foreground"
                  onClick={() => setReplyingComment(null)}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  disabled={isSendingReply || !replyText}
                  className="bg-primary hover:bg-primary text-white text-[10px] font-black uppercase tracking-widest px-6 gap-2 shadow-lg shadow-primary/20 disabled:opacity-50"
                  onClick={handleSendReply}
                >
                  {isSendingReply ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-3.5 w-3.5" />
                      Post Reply
                    </>
                  )}
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>

      <Dialog open={isEvergreenSettingsOpen} onOpenChange={setIsEvergreenSettingsOpen}>
        <DialogContent className="bg-card border-border text-foreground sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Settings className="h-5 w-5 text-primary" />
              Manage Evergreen Tags
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-xs text-muted-foreground leading-relaxed">
              Evergreen tags are global and appear in all your posts. You can add or remove them here.
            </p>
            <div className="flex flex-wrap gap-2 min-h-12 p-3 bg-background/30 rounded-xl border border-border">
              {Array.isArray(evergreenTags) && evergreenTags.map((tag, index) => (
                <Badge
                  key={index}
                  className="bg-green-600/10 text-green-400 border-green-500/20 px-2.5 py-1 text-xs font-bold flex items-center gap-1.5 group"
                >
                  {tag}
                  <X
                    className="h-3 w-3 cursor-pointer text-muted-foreground hover:text-red-400 transition-colors"
                    onClick={() => handleRemoveTag(tag, true)}
                  />
                </Badge>
              ))}
              {evergreenTags.length === 0 && (
                <p className="text-[10px] text-muted-foreground italic">No evergreen tags yet.</p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Add New Tag</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="e.g. #hashtag"
                  value={evergreenInput}
                  onChange={(e) => setEvergreenInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && evergreenInput.trim()) {
                      handleAddTag(evergreenInput.trim(), true);
                      setEvergreenInput('');
                    }
                  }}
                  className="bg-background border-border text-foreground h-10"
                />
                <Button
                  size="sm"
                  disabled={!evergreenInput.trim()}
                  onClick={() => {
                    handleAddTag(evergreenInput.trim(), true);
                    setEvergreenInput('');
                  }}
                  className="bg-primary hover:bg-primary text-white"
                >
                  Add
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setIsEvergreenSettingsOpen(false)} className="w-full bg-card hover:bg-muted text-white">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="bg-card border-border text-foreground max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2 text-rose-500">
              <AlertTriangle className="h-5 w-5" />
              Delete Post
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <p className="text-sm text-foreground">
              This will permanently remove the video file and thumbnail from our server to free up space. The post will be archived and hidden from your history.
            </p>

            <Alert variant="destructive" className="bg-rose-500/10 border-rose-500/20 text-rose-400">
              <Info className="h-4 w-4" />
              <AlertTitle className="text-xs font-black uppercase tracking-widest mb-1">Important</AlertTitle>
              <AlertDescription className="text-xs opacity-90">
                Deleting this post in {brand.name} **will not** remove it from your social media accounts. You must delete them manually on each platform.
              </AlertDescription>
            </Alert>

            {currentPost?.schedules && currentPost.schedules.some(s => s.externalPostId && s.status !== 'DELETED') && (
              <div className="pt-2">
                <button
                  onClick={() => setShowDeleteLinks(!showDeleteLinks)}
                  className="text-xs font-bold text-primary hover:text-primary/80 flex items-center gap-1 transition-colors"
                >
                  <ChevronRight className={cn("h-3 w-3 transition-transform", showDeleteLinks && "rotate-90")} />
                  View publications:
                </button>

                {showDeleteLinks && (
                  <div className="mt-2 space-y-2 pl-4 border-l border-border animate-in fade-in slide-in-from-left-1 duration-200">
                    {currentPost.schedules
                      .filter(s => s.externalPostId && s.status !== 'DELETED')
                      .map((s, i) => {
                        const platform = s.socialAccount?.platform?.toLowerCase() || '';
                        const handle = s.socialAccount?.handle || '';
                        const externalId = s.externalPostId || '';

                        let url = '#';
                        if (platform.includes('twitter') || platform.includes('x')) {
                          url = `https://x.com/i/web/status/${externalId}`;
                        } else if (platform.includes('tiktok')) {
                          url = `https://www.tiktok.com/@${handle}/video/${externalId}`;
                        } else if (platform.includes('bluesky')) {
                          const postPart = externalId.split('/').pop();
                          url = `https://bsky.app/profile/${handle}/post/${postPart}`;
                        } else if (platform.includes('telegram')) {
                          const cleanHandle = handle.startsWith('@') ? handle.substring(1) : handle;
                          url = `https://t.me/${cleanHandle}/${externalId}`;
                        }

                        return (
                          <a
                            key={i}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors group"
                          >
                            {getPlatformIcon(platform, "h-3 w-3")}
                            <span className="capitalize">{platform} publication</span>
                            <ExternalLink className="h-2.5 w-2.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </a>
                        );
                      })
                    }
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="ghost"
              onClick={() => setShowDeleteConfirm(false)}
              className="text-muted-foreground hover:text-foreground hover:bg-card"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmArchivePost}
              disabled={isPublishing}
              className="bg-rose-600 hover:bg-rose-700 text-white font-bold"
            >
              {isPublishing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Confirm Deletion'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
